// @ts-nocheck
// TODO: tighten types after migration
// This is the previous code from Post.js file

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./Post.css";
import "./AuthorPopup.css";

import { RichText } from "@graphcms/rich-text-react-renderer";
import { useParams, useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import DashedLines from "./ImageGalleryDots";
import { Helmet } from "react-helmet";
import TableOfContent from "./TableOfContent";
import SharePost from "./SharePost";
import BlogThoughts from "../Blog/BlogThoughts";  // wherever you put it
import Breadcrumbs from "../components/Breadcrumbs";
// eslint-disable-next-line no-unused-vars
import SocialLinks from "../components/SocialLinks";
import SidebarContentGroup from "../components/SidebarContentGroup";
// Note: Using references from main query instead of separate client calls

import SEO from "./SEO";
import useFavicon from "../utils/useFavicon";
import { getPostMetrics } from "./utils";
// eslint-disable-next-line no-unused-vars
import SEOAudit from "../utils/seoAudit";
import { API_ENDPOINTS } from "../config/api";
import "./PostMongoDb.css"; // Separate CSS for MongoDB posts
// eslint-disable-next-line no-unused-vars
import Header from "../Header";
// eslint-disable-next-line no-unused-vars
import Footer from "../footer";
// eslint-disable-next-line no-unused-vars
import { renderTiptapHTML } from "./tiptapRenderer";
import BookLoader from "../components/BookLoader";
import ListenToArticle from "./ListenToArticle";
import FontSizeSettings from "./FontSizeSettings";
import { TwitterTweetEmbed } from 'react-twitter-embed';
import ImageSlideshow from "../components/ImageSlideshow";
import FullScreenImageViewer from "../components/FullScreenImageViewer";
import ContinueReading from "./ContinueReading";
import InlineMediaVideo from "../components/InlineMediaVideo";
// Constant empty array for stable reference when no references exist
const EMPTY_REFERENCES = [];
const VIDEO_URL_REGEX = /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i;

const isVideoUrl = (url = "") => VIDEO_URL_REGEX.test(String(url));

const extractPlainTextFromNodes = (nodes = []) => {
    const walk = (nodeList) => {
        if (!Array.isArray(nodeList)) return '';
        return nodeList.map((node) => {
            if (!node) return '';
            if (node.type === 'text') return node.text || '';
            if (node.type === 'hardBreak') return ' ';
            return walk(node.content || []);
        }).join(' ');
    };

    return walk(nodes).replace(/\s+/g, ' ').trim();
};

const extractQuoteAndAuthor = (rawText = '') => {
    const text = String(rawText || '').replace(/\s+/g, ' ').trim();
    if (!text) return { quoteText: '', author: '' };

    const patterns = [
        /^(.*?)(?:\s*-\s*by\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/i,
        /^(.*?)(?:\s*-\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/,
        /^(.*?)(?:\s+by\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/i,
    ];

    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match && match[1] && match[2]) {
            const quoteText = match[1].trim();
            const author = match[2].trim();
            if (quoteText && author) {
                return { quoteText, author };
            }
        }
    }

    return { quoteText: text, author: '' };
};

// ========== LINK FAVICON COMPONENT ==========
// eslint-disable-next-line no-unused-vars
const LinkFavicon = ({ href }) => {
    if (!href) return null;
    try {
        const url = new URL(href, window.location.origin);
        // Skip same-site links and anchors
        if (url.hostname === window.location.hostname || href.startsWith('#')) return null;
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(url.hostname)}&sz=32`;
        return (
            <img
                src={faviconUrl}
                alt=""
                className="link-favicon"
                onError={(e) => { e.target.style.display = 'none'; }}
            />
        );
    } catch {
        return null;
    }
};

// ========== TWITTER EMBED COMPONENT ==========
// Option A: Fast & Lightweight (as recommended by user)
const TwitterEmbed = ({ url }) => {
    let tweetId = null;
    if (url) {
        try {
            const urlParts = url.split('/');
            tweetId = urlParts[urlParts.length - 1].split('?')[0];
        } catch (e) { console.error('Error parsing tweet URL:', e); }
    }

    useEffect(() => {
        if (!tweetId) return;
        // Function to force styles on Twitter iframes and their unnamed wrappers
        const forceTweetStyles = () => {
             // Target all direct children of tweet-container (the unnamed div)
            const containers = document.querySelectorAll('.tweet-container > div');
            containers.forEach(container => {
                container.style.setProperty('width', '100%', 'important');
                container.style.setProperty('max-width', '550px', 'important');
                container.style.setProperty('display', 'flex', 'important');
                container.style.setProperty('justify-content', 'center', 'important');
                container.style.setProperty('align-items', 'center', 'important');
            });

            // Target iframes
            const tweets = document.querySelectorAll('.tweet-container iframe');
            tweets.forEach(iframe => {
                iframe.style.setProperty('width', '100%', 'important');
                iframe.style.setProperty('margin', '0 auto', 'important');
                iframe.style.setProperty('display', 'block', 'important');
                iframe.style.setProperty('flex-grow', '0', 'important');
            });
        };

        // Initial attempt
        const timer = setTimeout(forceTweetStyles, 500);
        const timer2 = setTimeout(forceTweetStyles, 2000); // Backup

        // Hook into Twitter widget events if available
        if (window.twttr && window.twttr.events) {
            window.twttr.events.bind('rendered', (event) => {
                // Re-run the force script whenever a tweet renders
                forceTweetStyles();
            });
        }

        return () => {
            clearTimeout(timer);
            clearTimeout(timer2);
        };
    }, [tweetId]);

    if (!url || !tweetId) return null;

    return (
        <div className="tweet-container" data-tweet-url={url}>
            <TwitterTweetEmbed 
                tweetId={tweetId} 
                options={{ align: 'center', dnt: true, width: 'auto' }}
                placeholder={<div style={{padding: '20px', background: '#f0f0f0', textAlign: 'center'}}>Loading Tweet...</div>} 
            />
        </div>
    );
};

// ========== TWITTER SLIDESHOW COMPONENT ==========
// Clubs multiple consecutive Twitter embeds into a slider with arrow buttons
const TwitterSlideshow = ({ tweets = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    // Filter valid non-empty tweet URLs
    const validTweets = useMemo(() => {
        if (!Array.isArray(tweets)) return [];
        return tweets.filter(t => typeof t === 'string' && t.trim().length > 0);
    }, [tweets]);

    useEffect(() => {
        if (validTweets.length === 0) return;

        const forceTweetStyles = () => {
            const containers = document.querySelectorAll('.tweet-slideshow-container .tweet-container > div');
            containers.forEach(container => {
                container.style.setProperty('width', '100%', 'important');
                container.style.setProperty('max-width', '550px', 'important');
                container.style.setProperty('display', 'flex', 'important');
                container.style.setProperty('justify-content', 'center', 'important');
                container.style.setProperty('align-items', 'center', 'important');
            });

            const iframes = document.querySelectorAll('.tweet-slideshow-container iframe');
            iframes.forEach(iframe => {
                iframe.style.setProperty('width', '100%', 'important');
                iframe.style.setProperty('margin', '0 auto', 'important');
                iframe.style.setProperty('display', 'block', 'important');
                iframe.style.setProperty('flex-grow', '0', 'important');
            });
        };

        const timer1 = setTimeout(forceTweetStyles, 300);
        const timer2 = setTimeout(forceTweetStyles, 1000);
        const timer3 = setTimeout(forceTweetStyles, 2500);

        if (window.twttr && window.twttr.events) {
            window.twttr.events.bind('rendered', forceTweetStyles);
        }

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [currentIndex, validTweets]);

    if (!validTweets || validTweets.length === 0) return null;

    // Fallback if single tweet
    if (validTweets.length === 1) {
        return <TwitterEmbed url={validTweets[0]} />;
    }

    const handleNext = () => {
        setCurrentIndex(prev => (prev + 1) % validTweets.length);
    };

    const handlePrev = () => {
        setCurrentIndex(prev => (prev - 1 + validTweets.length) % validTweets.length);
    };

    const getOrdinalSuffix = (i) => {
        const j = i % 10, k = i % 100;
        if (j === 1 && k !== 11) return "st";
        if (j === 2 && k !== 12) return "nd";
        if (j === 3 && k !== 13) return "rd";
        return "th";
    };

    return (
        <div className="tweet-slideshow-container mongodb-image-post-area my-8 block clear-both relative flow-root w-full max-w-[580px] mx-auto p-3 rounded-2xl bg-[var(--card-bg-subtle,rgba(255,255,255,0.03))] border border-[var(--border-color,rgba(0,0,0,0.08))] shadow-sm transition-all duration-300">
            {/* Header bar with icon and dot indicators */}
            <div className="flex items-center justify-between px-2 mb-2 pb-2 border-b border-[var(--border-color,rgba(0,0,0,0.06))] text-xs font-semibold tracking-wide text-[#1da1f2] dark:text-[#1d9bf0]">
                <div className="flex items-center gap-1.5">
                    <i className="fi fi-brands-twitter-alt text-[14px]"></i>
                    <span>Twitter Posts ({currentIndex + 1}/{validTweets.length})</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {validTweets.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Go to tweet ${idx + 1}`}
                            className={`h-2 rounded-full transition-all duration-200 p-0 border-0 cursor-pointer ${
                                idx === currentIndex
                                    ? 'bg-[#1da1f2] w-4'
                                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 w-2'
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* Tweets Container */}
            <div className="tweet-slideshow-wrapper relative w-full min-h-[350px] flex justify-center items-center py-1">
                {validTweets.map((tweetUrl, idx) => (
                    <div
                        key={idx}
                        className="tweet-slide-item w-full"
                        style={{ display: idx === currentIndex ? 'block' : 'none' }}
                    >
                        <TwitterEmbed url={tweetUrl} />
                    </div>
                ))}
            </div>

            {/* Footer Row with Navigation Arrow Buttons */}
            <div className="mongodb-image-info-row flex justify-between items-center w-full gap-3 pt-2 mt-1 border-t border-[var(--border-color,rgba(0,0,0,0.06))]">
                <p className="mongodb-image-title m-0 text-left flex-1 text-sm leading-relaxed text-[var(--post-content-font,#333)]">
                    <strong>
                        {currentIndex + 1}<sup>{getOrdinalSuffix(currentIndex + 1)}</sup> Twitter Post
                    </strong>
                    <span className="text-[#777] dark:text-[#aaa] font-normal text-xs ml-2">
                        ({currentIndex + 1} of {validTweets.length})
                    </span>
                </p>

                <div className="mongodb-image-post-controls flex gap-2 m-0 flex-shrink-0 items-center">
                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        aria-label="Previous Twitter post"
                        title="Previous post"
                    >
                        <i className="fi fi-rr-caret-left text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>

                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handleNext}
                        disabled={currentIndex === validTweets.length - 1}
                        aria-label="Next Twitter post"
                        title="Next post"
                    >
                        <i className="fi fi-rr-caret-right text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper to extract Twitter / X post URL from various node formats
const extractTwitterUrl = (node) => {
    if (!node) return null;

    if (node.type === 'twitterEmbed' && node.attrs && node.attrs.url) {
        return node.attrs.url;
    }

    if (node.type === 'paragraph') {
        let textContent = '';
        if (node.content && Array.isArray(node.content)) {
            node.content.forEach(child => {
                if (child.type === 'text') {
                    textContent += child.text || '';
                }
            });
        } else if (typeof node.text === 'string') {
            textContent = node.text;
        }
        textContent = textContent.trim();

        if (textContent) {
            const tweetMatch = textContent.match(/(?:<!--|&lt;!--)\s*TWEET:\s*(https:\/\/(?:twitter\.com|x\.com)\/[^ ]+)\s*(?:-->|--&gt;)|\[TWEET_EMBED:\s*(https:\/\/(?:twitter\.com|x\.com)\/[^ \]]+)\s*\]/);
            if (tweetMatch) {
                return tweetMatch[1] || tweetMatch[2];
            }

            const urlMatch = textContent.match(/^(https?:\/\/(?:twitter\.com|x\.com)\/[A-Za-z0-9_]+\/status\/\d+)/);
            if (urlMatch) {
                return urlMatch[1];
            }
        }

        if (node.content && node.content.length === 1 && node.content[0].marks) {
            const linkMark = node.content[0].marks.find(m => m.type === 'link');
            if (linkMark && linkMark.attrs && linkMark.attrs.href) {
                const href = linkMark.attrs.href;
                if (/^https?:\/\/(?:twitter\.com|x\.com)\/[A-Za-z0-9_]+\/status\/\d+/.test(href)) {
                    return href;
                }
            }
        }
    }

    return null;
};

// Helper to check if a paragraph node is empty/whitespace
const isEmptyParagraph = (node) => {
    if (!node || node.type !== 'paragraph') return false;
    if (!node.content || node.content.length === 0) return true;
    let text = '';
    node.content.forEach(c => {
        if (c.type === 'text') text += c.text || '';
    });
    return text.trim().length === 0;
};

// // ========== INSTAGRAM EMBED COMPONENT ==========
const InstagramEmbed = ({ url }) => {
    if (!url) return null;

    // Normalize URL to ensure it ends with /embed
    let embedUrl = url;
    if (!embedUrl.endsWith('/')) embedUrl += '/';
    if (!embedUrl.includes('/embed')) embedUrl += 'embed';

    return (
        <div className="instagram-embed-container" data-instagram-url={url} style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
            <iframe
                src={embedUrl}
                width="100%"
                maxWidth="540px"
                height="600px"
                frameBorder="0"
                scrolling="no"
                allowTransparency="true"
                allow="encrypted-media"
                style={{
                    border: '1px solid #dbdbdb',
                    borderRadius: '12px',
                    maxWidth: '540px',
                    width: '100%',
                    background: 'white'
                }}
                title="Instagram Post"
            ></iframe>
        </div>
    );
};

// ========== INSTAGRAM SLIDESHOW COMPONENT ==========
// Clubs multiple consecutive Instagram posts into a slider with arrow buttons
const InstagramSlideshow = ({ posts = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const validPosts = useMemo(() => {
        if (!Array.isArray(posts)) return [];
        return posts.filter(p => typeof p === 'string' && p.trim().length > 0);
    }, [posts]);

    if (!validPosts || validPosts.length === 0) return null;

    if (validPosts.length === 1) {
        return <InstagramEmbed url={validPosts[0]} />;
    }

    const handleNext = () => {
        setCurrentIndex(prev => (prev + 1) % validPosts.length);
    };

    const handlePrev = () => {
        setCurrentIndex(prev => (prev - 1 + validPosts.length) % validPosts.length);
    };

    const getOrdinalSuffix = (i) => {
        const j = i % 10, k = i % 100;
        if (j === 1 && k !== 11) return "st";
        if (j === 2 && k !== 12) return "nd";
        if (j === 3 && k !== 13) return "rd";
        return "th";
    };

    return (
        <div className="instagram-slideshow-container mongodb-image-post-area my-8 block clear-both relative flow-root w-full max-w-[580px] mx-auto p-3 rounded-2xl bg-[var(--card-bg-subtle,rgba(255,255,255,0.03))] border border-[var(--border-color,rgba(0,0,0,0.08))] shadow-sm transition-all duration-300">
            {/* Header bar with Instagram brand accent and dots */}
            <div className="flex items-center justify-between px-2 mb-2 pb-2 border-b border-[var(--border-color,rgba(0,0,0,0.06))] text-xs font-semibold tracking-wide text-[#e1306c] dark:text-[#f77737]">
                <div className="flex items-center gap-1.5">
                    <i className="fi fi-brands-instagram text-[14px]"></i>
                    <span>Instagram Posts ({currentIndex + 1}/{validPosts.length})</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {validPosts.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Go to Instagram post ${idx + 1}`}
                            className={`h-2 rounded-full transition-all duration-200 p-0 border-0 cursor-pointer ${
                                idx === currentIndex
                                    ? 'bg-[#e1306c] w-4'
                                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 w-2'
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* Posts Stack Container */}
            <div className="instagram-slideshow-wrapper relative w-full flex justify-center items-center py-1 min-h-[500px]">
                {validPosts.map((postUrl, idx) => (
                    <div
                        key={idx}
                        className="instagram-slide-item w-full"
                        style={{ display: idx === currentIndex ? 'block' : 'none' }}
                    >
                        <InstagramEmbed url={postUrl} />
                    </div>
                ))}
            </div>

            {/* Footer Row with Navigation Arrow Buttons */}
            <div className="mongodb-image-info-row flex justify-between items-center w-full gap-3 pt-2 mt-1 border-t border-[var(--border-color,rgba(0,0,0,0.06))]">
                <p className="mongodb-image-title m-0 text-left flex-1 text-sm leading-relaxed text-[var(--post-content-font,#333)]">
                    <strong>
                        {currentIndex + 1}<sup>{getOrdinalSuffix(currentIndex + 1)}</sup> Instagram Post
                    </strong>
                    <span className="text-[#777] dark:text-[#aaa] font-normal text-xs ml-2">
                        ({currentIndex + 1} of {validPosts.length})
                    </span>
                </p>

                <div className="mongodb-image-post-controls flex gap-2 m-0 flex-shrink-0 items-center">
                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        aria-label="Previous Instagram post"
                        title="Previous post"
                    >
                        <i className="fi fi-rr-caret-left text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>

                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handleNext}
                        disabled={currentIndex === validPosts.length - 1}
                        aria-label="Next Instagram post"
                        title="Next post"
                    >
                        <i className="fi fi-rr-caret-right text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper to extract Instagram post URL from various node formats
const extractInstagramUrl = (node) => {
    if (!node) return null;

    if (node.type === 'instagramEmbed' && node.attrs && node.attrs.url) {
        return node.attrs.url;
    }

    if (node.type === 'paragraph') {
        let textContent = '';
        if (node.content && Array.isArray(node.content)) {
            node.content.forEach(child => {
                if (child.type === 'text') {
                    textContent += child.text || '';
                }
            });
        } else if (typeof node.text === 'string') {
            textContent = node.text;
        }
        textContent = textContent.trim();

        if (textContent) {
            const urlMatch = textContent.match(/(https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[A-Za-z0-9_-]+)/i);
            if (urlMatch) {
                return urlMatch[1];
            }
        }

        if (node.content && node.content.length === 1 && node.content[0].marks) {
            const linkMark = node.content[0].marks.find(m => m.type === 'link');
            if (linkMark && linkMark.attrs && linkMark.attrs.href) {
                const href = linkMark.attrs.href;
                if (/^https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[A-Za-z0-9_-]+/i.test(href)) {
                    return href;
                }
            }
        }
    }

    return null;
};

// ========== YOUTUBE EMBED COMPONENT ==========
const YouTubeEmbed = ({ videoId }) => {
    if (!videoId) return null;
    
    return (
      <div className="youtube-embed-wrapper" data-youtube-videoid={videoId}>
        <iframe
          className="youtube-embed-iframe"
          src={`https://www.youtube-nocookie.com/embed/${videoId}`}
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
};

// ========== YOUTUBE SLIDESHOW COMPONENT ==========
// Clubs multiple consecutive YouTube videos into a slider with arrow buttons
const YouTubeSlideshow = ({ videoIds = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const validVideoIds = useMemo(() => {
        if (!Array.isArray(videoIds)) return [];
        return videoIds.filter(id => typeof id === 'string' && id.trim().length > 0);
    }, [videoIds]);

    if (!validVideoIds || validVideoIds.length === 0) return null;

    if (validVideoIds.length === 1) {
        return <YouTubeEmbed videoId={validVideoIds[0]} />;
    }

    const handleNext = () => {
        setCurrentIndex(prev => (prev + 1) % validVideoIds.length);
    };

    const handlePrev = () => {
        setCurrentIndex(prev => (prev - 1 + validVideoIds.length) % validVideoIds.length);
    };

    const getOrdinalSuffix = (i) => {
        const j = i % 10, k = i % 100;
        if (j === 1 && k !== 11) return "st";
        if (j === 2 && k !== 12) return "nd";
        if (j === 3 && k !== 13) return "rd";
        return "th";
    };

    return (
        <div className="youtube-slideshow-container mongodb-image-post-area my-8 block clear-both relative flow-root w-full max-w-[700px] mx-auto p-3 rounded-2xl bg-[var(--card-bg-subtle,rgba(255,255,255,0.03))] border border-[var(--border-color,rgba(0,0,0,0.08))] shadow-sm transition-all duration-300">
            {/* Header bar with YouTube red accent and dots */}
            <div className="flex items-center justify-between px-2 mb-2 pb-2 border-b border-[var(--border-color,rgba(0,0,0,0.06))] text-xs font-semibold tracking-wide text-[#ff0000] dark:text-[#ff4e4e]">
                <div className="flex items-center gap-1.5">
                    <i className="fi fi-brands-youtube text-[14px]"></i>
                    <span>YouTube Videos ({currentIndex + 1}/{validVideoIds.length})</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {validVideoIds.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Go to YouTube video ${idx + 1}`}
                            className={`h-2 rounded-full transition-all duration-200 p-0 border-0 cursor-pointer ${
                                idx === currentIndex
                                    ? 'bg-[#ff0000] w-4'
                                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 w-2'
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* Video Stack Container */}
            <div className="youtube-slideshow-wrapper relative w-full flex justify-center items-center py-1">
                {validVideoIds.map((vId, idx) => (
                    <div
                        key={idx}
                        className="youtube-slide-item w-full"
                        style={{ display: idx === currentIndex ? 'block' : 'none' }}
                    >
                        <YouTubeEmbed videoId={vId} />
                    </div>
                ))}
            </div>

            {/* Footer Row with Navigation Arrow Buttons */}
            <div className="mongodb-image-info-row flex justify-between items-center w-full gap-3 pt-2 mt-1 border-t border-[var(--border-color,rgba(0,0,0,0.06))]">
                <p className="mongodb-image-title m-0 text-left flex-1 text-sm leading-relaxed text-[var(--post-content-font,#333)]">
                    <strong>
                        {currentIndex + 1}<sup>{getOrdinalSuffix(currentIndex + 1)}</sup> YouTube Video
                    </strong>
                    <span className="text-[#777] dark:text-[#aaa] font-normal text-xs ml-2">
                        ({currentIndex + 1} of {validVideoIds.length})
                    </span>
                </p>

                <div className="mongodb-image-post-controls flex gap-2 m-0 flex-shrink-0 items-center">
                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        aria-label="Previous YouTube video"
                        title="Previous video"
                    >
                        <i className="fi fi-rr-caret-left text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>

                    <button
                        className="mongodb-nav-btn appearance-none bg-white dark:bg-[#222] border border-[#e1e1e1] dark:border-[#444] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-md active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        onClick={handleNext}
                        disabled={currentIndex === validVideoIds.length - 1}
                        aria-label="Next YouTube video"
                        title="Next video"
                    >
                        <i className="fi fi-rr-caret-right text-[12px] flex items-center justify-center text-[#555] dark:text-[#ccc] w-full h-full"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper to extract YouTube video ID from various node formats
const extractYouTubeVideoId = (node) => {
    if (!node) return null;

    if (node.type === 'youtubeEmbed') {
        if (node.attrs && node.attrs.videoId) return node.attrs.videoId;
        if (node.attrs && node.attrs.url) {
            const match = node.attrs.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (match) return match[1];
        }
    }

    if (node.type === 'paragraph') {
        let textContent = '';
        if (node.content && Array.isArray(node.content)) {
            node.content.forEach(child => {
                if (child.type === 'text') {
                    textContent += child.text || '';
                }
            });
        } else if (typeof node.text === 'string') {
            textContent = node.text;
        }
        textContent = textContent.trim();

        if (textContent) {
            const match = textContent.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (match) {
                return match[1];
            }
        }

        if (node.content && node.content.length === 1 && node.content[0].marks) {
            const linkMark = node.content[0].marks.find(m => m.type === 'link');
            if (linkMark && linkMark.attrs && linkMark.attrs.href) {
                const match = linkMark.attrs.href.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                if (match) return match[1];
            }
        }
    }

    return null;
};

// ========== BLOG EMBED COMPONENT ==========
const BlogEmbed = ({ url, title, image }) => {
    if (!url) return null;

    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="blog-embed-frontend">
            <div className="blog-embed-image-wrapper">
                 {image ? (
                     <img src={image} alt={title} className="blog-embed-image" />
                 ) : (
                    <div className="blog-embed-placeholder">
                        <i className="fi fi-sr-journal-alt"></i>
                    </div>
                 )}
            </div>
            <div className="blog-embed-content">
                <div className="blog-embed-title">{title || "Blog Post"}</div>
                <div className="blog-embed-url">{url}</div>
            </div>
        </a>
    );
};

// ========== BLOG LINK COMPONENT ==========
const BlogLink = ({ url, text }) => {
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="blog-link-frontend">
             <span className="blog-icon-frontend">
                <i className="fi fi-sr-journal-alt"></i>
             </span>
            <span className="blog-text-frontend">{text}</span>
        </a>
    );
};

// ========== JSON CONTENT RENDERER ==========
// Helper to render text with marks but IGNORING links (so we can wrap them manually)
const renderTextContentOnly = (node, index) => {
    if (!node) return null;
    let textElement = node.text;
    
    if (node.marks) {
        node.marks.forEach((mark, mIndex) => {
            const key = `${index}-${mIndex}`;
            if (mark.type === 'bold') textElement = <strong key={key}>{textElement}</strong>;
            if (mark.type === 'italic') textElement = <em key={key}>{textElement}</em>;
            if (mark.type === 'underline') textElement = <u key={key}>{textElement}</u>;
            if (mark.type === 'code') textElement = <code key={key}>{textElement}</code>;
            // Skip link mark here - we handle it in the grouping logic
        });
    }
    
    return <span key={index}>{textElement}</span>;
};

// Helper to group adjacent link nodes
const renderGroupedContent = (content) => {
    if (!content) return null;
    const result = [];
    let i = 0;
    
    while (i < content.length) {
        const node = content[i];
        const linkMark = node.type === 'text' && node.marks ? node.marks.find(m => m.type === 'link') : null;
        
        if (linkMark) {
            // Found a link, look ahead for more nodes with SAME link
            const group = [node];
            let j = i + 1;
            while (j < content.length) {
                const nextNode = content[j];
                const nextLinkMark = nextNode.type === 'text' && nextNode.marks ? nextNode.marks.find(m => m.type === 'link') : null;
                
                // Compare href to ensure it's the same link
                if (nextLinkMark && nextLinkMark.attrs.href === linkMark.attrs.href) {
                    group.push(nextNode);
                    j++;
                } else {
                    break;
                }
            }
            
            // Render the group
            result.push(
                /* eslint-disable no-loop-func */
                <a key={i} href={linkMark.attrs.href} target={linkMark.attrs.target || '_blank'} rel="noopener noreferrer">
                    <LinkFavicon href={linkMark.attrs.href} />
                    {group.map((n, idx) => renderTextContentOnly(n, i + idx))}
                    <i className="fi fi-rr-arrow-up-right-from-square" style={{ marginLeft: "4px", fontSize: "0.7em", textDecoration: "none", display: "inline-block" }}></i>
                </a>
                /* eslint-enable no-loop-func */
            );
            
            // Advance index
            i = j;
        } else {
            // Not a link, render normally
            result.push(node.type === 'text' ? renderText(node, i) : renderNode(node, i));
            i++;
        }
    }
    return result;
};

// Component for single image with skeleton loader
const ImageWithSkeleton = ({ src, alt, title }) => {
    const [loaded, setLoaded] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [triggerRect, setTriggerRect] = useState(null);
    const imgRef = useRef(null);

    // Reset loaded state if src changes
    useEffect(() => {
        setLoaded(false);
    }, [src]);

    const handleMaximize = () => {
        if (imgRef.current) {
            setTriggerRect(imgRef.current.getBoundingClientRect());
        }
        setIsFullScreen(true);
    };

    // Robust check for already loaded images (cache)
    useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            setLoaded(true);
        }
    }, [src]);

    return (
        <React.Fragment>
            <div className="mongodb-image-post-area">
                <div className="mongodb-image-wrapper">
                    {!loaded && <div className="blog-image-skeleton-loader"></div>}
                    <img 
                        ref={imgRef}
                        className="mongodb-image-post-view"
                        src={src} 
                        alt={alt} 
                        title={title}
                        style={{
                            ...(!loaded ? { position: 'absolute', top: 0, left: 0, opacity: 0, width: '100%', height: '100%' } : {}),
                            cursor: 'zoom-in' 
                        }}
                        onLoad={() => setLoaded(true)}
                        onError={() => setLoaded(true)} // Hide skeleton on error
                        onClick={handleMaximize}
                    />
                </div>
                
                {loaded && (
                    <div className="mongodb-image-info-row flex justify-between items-start w-full gap-4 mt-3">
                        <p className="mongodb-image-title flex-1 m-0 text-left italic border-r-[3.5px] border-[#ccc] pr-3 mr-2 leading-relaxed">
                            {title || alt}
                        </p>
                        <div className="mongodb-image-post-controls flex-shrink-0 flex items-center gap-2">
                            <button 
                                className="mongodb-nav-btn maximize-btn"
                                onClick={handleMaximize}
                                aria-label="Maximize image"
                            >
                                <i className="fi fi-br-expand"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            <FullScreenImageViewer 
                isOpen={isFullScreen} 
                onClose={() => setIsFullScreen(false)} 
                images={[{ src, alt, title }]} 
                initialIndex={0}
                triggerRect={triggerRect}
            />
        </React.Fragment>
    );
};

const renderText = (node, index) => {
    if (!node) return null;
    let textElement = node.text;
    
    if (node.marks) {
        // Apply marks in order
        node.marks.forEach((mark, mIndex) => {
            const key = `${index}-${mIndex}`;
            if (mark.type === 'bold') textElement = <strong key={key}>{textElement}</strong>;
            if (mark.type === 'italic') textElement = <em key={key}>{textElement}</em>;
            if (mark.type === 'underline') textElement = <u key={key}>{textElement}</u>;
            if (mark.type === 'code') textElement = <code key={key}>{textElement}</code>;
            if (mark.type === 'link') {
                textElement = (
                    <a key={key} href={mark.attrs.href} target={mark.attrs.target || '_blank'} rel="noopener noreferrer">
                        <LinkFavicon href={mark.attrs.href} />
                        {textElement}
                    </a>
                );
            }
        });
    }
    
    return <span key={index}>{textElement}</span>;
};

const renderNode = (node, index) => {
    if (!node) return null;
    
    switch (node.type) {
        case 'doc':
            return <React.Fragment key={index}>{node.content && node.content.map((child, i) => renderNode(child, i))}</React.Fragment>;
            
        case 'paragraph':
            const singleTweetUrl = extractTwitterUrl(node);
            if (singleTweetUrl) {
                return <TwitterEmbed key={index} url={singleTweetUrl} />;
            }
            const singleInstaUrl = extractInstagramUrl(node);
            if (singleInstaUrl) {
                return <InstagramEmbed key={index} url={singleInstaUrl} />;
            }
            const singleYtId = extractYouTubeVideoId(node);
            if (singleYtId) {
                return <YouTubeEmbed key={index} videoId={singleYtId} />;
            }
            // Check if it's an image-only paragraph (common in some editors)
            if (node.content && node.content.length === 1 && node.content[0].type === 'image') {
                return renderNode(node.content[0], index);
            }
            return (
                <p key={index} className="rt rt-para">
                    {renderGroupedContent(node.content) || <br />}
                </p>
            );
            
        case 'heading':
            const Level = `h${node.attrs.level || 1}`;
            // Generate simple ID
            const textContent = node.content ? node.content.map(n => n.text).join('') : '';
            const id = textContent.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '') || `h-${index}`;
            
            return (
                <Level key={index} id={id} className={`rt rt-${Level}`}>
                    {renderGroupedContent(node.content)}
                </Level>
            );
            
        case 'twitterEmbed':
            return <TwitterEmbed key={index} url={node.attrs.url} />;

        case 'twitterSlideshow':
            return <TwitterSlideshow key={index} tweets={node.tweets} />;

        case 'instagramEmbed':
            return <InstagramEmbed key={index} url={node.attrs.url} />;

        case 'instagramSlideshow':
            return <InstagramSlideshow key={index} posts={node.posts} />;

        case 'youtubeEmbed':
            return <YouTubeEmbed key={index} videoId={node.attrs.videoId || (node.attrs && extractYouTubeVideoId(node))} />;

        case 'youtubeSlideshow':
            return <YouTubeSlideshow key={index} videoIds={node.videoIds} />;

        case 'videoAsset':
            return (
                <InlineMediaVideo
                    key={index}
                    src={node.attrs.src || node.attrs['data-video-asset-src']}
                    title={node.attrs.title || node.attrs['data-filename'] || ''}
                    poster={node.attrs.poster || node.attrs['data-video-asset-poster'] || ''}
                />
            );

        case 'podcastLink':
            return <PodcastLink key={index} url={node.attrs.url} text={node.attrs.text} />;

        case 'blogEmbed':
            return <BlogEmbed key={index} url={node.attrs.url} title={node.attrs.title} image={node.attrs.image} />;

        case 'blogLink':
            return <BlogLink key={index} url={node.attrs.url} text={node.attrs.text} />;

            
        case 'image':
            if (isVideoUrl(node.attrs.src || node.attrs.url)) {
                return (
                    <InlineMediaVideo
                        key={index}
                        src={node.attrs.src || node.attrs.url}
                        title={node.attrs.title || node.attrs.alt || ''}
                        poster={node.attrs.poster || ''}
                    />
                );
            }
            return (
                <ImageWithSkeleton 
                    key={index} 
                    src={node.attrs.src || node.attrs.url} 
                    alt={node.attrs.alt || ''} 
                    title={node.attrs.title || ''} 
                />
            );
            
        case 'bulletList':
            return <ul key={index} className="rt rt-ul">{node.content && node.content.map((child, i) => renderNode(child, i))}</ul>;
            
        case 'orderedList':
            return <ol key={index} className="rt rt-ol">{node.content && node.content.map((child, i) => renderNode(child, i))}</ol>;
            
        case 'listItem':
            return <li key={index} className="rt rt-li">{node.content && node.content.map((child, i) => renderNode(child, i))}</li>;
            
        case 'blockquote':
            let author = '';
            let clonedContent = node.content ? JSON.parse(JSON.stringify(node.content)) : [];

            const extractAuthorFromLastNode = (nodes) => {
                if (!Array.isArray(nodes) || nodes.length === 0) return false;
                for (let i = nodes.length - 1; i >= 0; i--) {
                    const child = nodes[i];
                    if (child.type === 'text') {
                        const patterns = [
                            /(.*?)(?:(\s*-\s*by\s+)([A-Za-z][\w .,'"-]{1,80}))\s*$/i,
                            /(.*?)(?:(\s*-\s*)([A-Za-z][\w .,'"-]{1,80}))\s*$/,
                            /(.*?)(?:(\s+by\s+)([A-Za-z][\w .,'"-]{1,80}))\s*$/i,
                        ];
                        for (const pattern of patterns) {
                            const match = child.text.match(pattern);
                            if (match && match[1] !== undefined && match[2] !== undefined && match[3] !== undefined) {
                                child.text = match[1].trim();
                                author = (match[2].trim() + ' ' + match[3].trim()).trim();
                                return true;
                            }
                        }
                    } else if (child.content) {
                        if (extractAuthorFromLastNode(child.content)) {
                            return true;
                        }
                    }
                }
                return false;
            };
            extractAuthorFromLastNode(clonedContent);

            return (
                <blockquote
                    key={index}
                    className="relative w-[95%] max-w-[1000px] mx-[1%] my-[6%]"
                >
                    <div className="relative z-[1] w-full">
                        <div 
                            className="text-left text-[clamp(1.15rem,1.5vw,1.5rem)] leading-[1.25] tracking-[-0.015em] text-[var(--post-heading-font)] [&>p]:!mb-0 [&>p]:!mt-0" 
                            style={{ fontFamily: '"Lora", serif', fontWeight: 800 }}
                        >
                            {clonedContent.map((child, i) => renderNode(child, i))}
                        </div>
                        {author ? (
                            <div className="mt-[15px] flex justify-end w-full">
                                <div className="text-right text-[clamp(0.85rem,1.2vw,1.05rem)] font-bold text-[var(--post-heading-font)] opacity-90 !font-sans">
                                    {author}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </blockquote>
            );
            
         case 'codeBlock':
            return (
                <pre key={index}>
                    <code>{node.content && node.content.map(n => n.text).join('')}</code>
                </pre>
            );
            
        case 'horizontalRule':
            return <hr key={index} />;

        // Table support
         case 'table':
            return (
                <div key={index} className="table-wrapper">
                    <table className="rt rt-table">
                        <tbody className="custom-tbody">
                            {node.content && node.content.map((child, i) => renderNode(child, i))}
                        </tbody>
                    </table>
                </div>
            );
        case 'tableRow':
             return <tr key={index} className="custom-tr">{node.content && node.content.map((child, i) => renderNode(child, i))}</tr>;
        case 'tableHeader':
             return <th key={index} className="custom-th">{node.content && node.content.map((child, i) => renderNode(child, i))}</th>;
        case 'tableCell':
             return <td key={index} className="custom-td">{node.content && node.content.map((child, i) => renderNode(child, i))}</td>;
        
        case 'imageSlideshow':
             return <ImageSlideshow key={index} images={node.images} />;
        
        default:
            // Fallback for unknown nodes
            if (node.content) {
                return <div key={index}>{node.content.map((child, i) => renderNode(child, i))}</div>;
            }
            return null;
    }
};

const JsonContentRenderer = ({ content }) => {
    let nodes = [];
    if (!content) return null;
    
    // Normalize content
    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            nodes = parsed.content || [];
        } catch (e) {
            console.error('Failed to parse JSON content:', e);
            // If it's just a string, try rendering it as HTML if it looks like HTML, or text
            if (content.trim().startsWith('<')) {
                 return <div dangerouslySetInnerHTML={{ __html: content }} />;
            }
            return <p>{content}</p>;
        }
    } else if (content.type === 'doc') {
        nodes = content.content || [];
    } else if (Array.isArray(content)) {
        nodes = content;
    } else if (content.content) {
        // Handle case where content is an object but not 'doc' typed explicitly
         nodes = content.content;
    } else if (content.children) {
        // Handle GraphCMS-like structure if it sneaks in
        nodes = content.children;
    }

    if (!Array.isArray(nodes)) {
        return null;
    }

    // Group consecutive images/videos, twitter posts, instagram posts, and youtube videos
    const processedNodes = [];
    let mediaGroup = [];
    let tweetGroup = [];
    let instaGroup = [];
    let youtubeGroup = [];

    const isMediaNode = (node) => {
        if (!node) return false;
        if (node.type === 'image' || node.type === 'videoAsset') return true;
        if (node.type === 'paragraph' && node.content && node.content.length === 1) {
            const childType = node.content[0].type;
            return childType === 'image' || childType === 'videoAsset';
        }
        return false;
    };

    const extractMediaParams = (node) => {
        let attrs = {};
        let isVideo = false;
        let nodeType = '';

        if (node.type === 'image' || node.type === 'videoAsset') {
            attrs = node.attrs || {};
            nodeType = node.type;
        } else if (node.type === 'paragraph' && node.content && node.content.length === 1) {
            attrs = node.content[0].attrs || {};
            nodeType = node.content[0].type;
        }

        const src = attrs.src || attrs.url || attrs['data-video-asset-src'] || '';
        isVideo = nodeType === 'videoAsset' || isVideoUrl(src);

        return {
            src,
            alt: attrs.alt || attrs.title || attrs['data-filename'] || '',
            title: attrs.title || attrs.alt || attrs['data-filename'] || '',
            poster: attrs.poster || attrs['data-video-asset-poster'] || '',
            isVideo
        };
    };

    const flushMediaGroup = () => {
        if (mediaGroup.length > 0) {
            if (mediaGroup.length === 1) {
                processedNodes.push(mediaGroup[0]);
            } else {
                processedNodes.push({
                    type: 'imageSlideshow',
                    images: mediaGroup.map(n => extractMediaParams(n))
                });
            }
            mediaGroup = [];
        }
    };

    const flushTweetGroup = () => {
        if (tweetGroup.length > 0) {
            if (tweetGroup.length === 1) {
                processedNodes.push(tweetGroup[0].node);
            } else {
                processedNodes.push({
                    type: 'twitterSlideshow',
                    tweets: tweetGroup.map(item => item.url)
                });
            }
            tweetGroup = [];
        }
    };

    const flushInstaGroup = () => {
        if (instaGroup.length > 0) {
            if (instaGroup.length === 1) {
                processedNodes.push(instaGroup[0].node);
            } else {
                processedNodes.push({
                    type: 'instagramSlideshow',
                    posts: instaGroup.map(item => item.url)
                });
            }
            instaGroup = [];
        }
    };

    const flushYoutubeGroup = () => {
        if (youtubeGroup.length > 0) {
            if (youtubeGroup.length === 1) {
                processedNodes.push(youtubeGroup[0].node);
            } else {
                processedNodes.push({
                    type: 'youtubeSlideshow',
                    videoIds: youtubeGroup.map(item => item.videoId)
                });
            }
            youtubeGroup = [];
        }
    };

    const flushAllGroups = () => {
        flushMediaGroup();
        flushTweetGroup();
        flushInstaGroup();
        flushYoutubeGroup();
    };

    nodes.forEach(node => {
        const twitterUrl = extractTwitterUrl(node);
        const instagramUrl = extractInstagramUrl(node);
        const youtubeVideoId = extractYouTubeVideoId(node);

        if (twitterUrl) {
            flushMediaGroup();
            flushInstaGroup();
            flushYoutubeGroup();
            tweetGroup.push({ node, url: twitterUrl });
        } else if (instagramUrl) {
            flushMediaGroup();
            flushTweetGroup();
            flushYoutubeGroup();
            instaGroup.push({ node, url: instagramUrl });
        } else if (youtubeVideoId) {
            flushMediaGroup();
            flushTweetGroup();
            flushInstaGroup();
            youtubeGroup.push({ node, videoId: youtubeVideoId });
        } else if (isMediaNode(node)) {
            flushTweetGroup();
            flushInstaGroup();
            flushYoutubeGroup();
            mediaGroup.push(node);
        } else if (isEmptyParagraph(node) && (tweetGroup.length > 0 || instaGroup.length > 0 || youtubeGroup.length > 0 || mediaGroup.length > 0)) {
            // Ignore empty paragraph while accumulating consecutive items in any active group
        } else {
            flushAllGroups();
            processedNodes.push(node);
        }
    });
    flushAllGroups();

    return (
        <div className="rich-text-comp">
            {processedNodes.map((node, i) => renderNode(node, i))}
        </div>
    );
};



// ========== MONGODB POST RENDERER COMPONENT START ==========
// Separate component to render MongoDB posts with different class names
function MongoDbPostRenderer({ post, get_slug }) {
    const [commentCount, setCommentCount] = useState(0);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [selectedAuthorIndex, setSelectedAuthorIndex] = useState(0); 
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [tocVisible, setTocVisible] = useState(false);
    const tocVisibleRef = useRef(false);
    const [value, setValue] = useState(0);
    const [fontSize, setFontSize] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem('post-font-size');
            if (saved) return saved;
            return window.innerWidth <= 768 ? 'small' : 'medium';
        }
        return 'medium';
    }); 
    const [readingFamily, setReadingFamily] = useState(() => {
        if (typeof window !== 'undefined') {
            return sessionStorage.getItem('post-reading-family') || 'standard';
        }
        return 'standard';
    }); // 'standard', 'warm'

    // Set default font size for mobile on mount if not saved, and sync settings/page active state
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem('post-font-size');
            if (!saved) {
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                    setFontSize('small');
                }
            }
        }

        const handleFontSettingsUpdate = (e) => {
            if (e.detail) {
                if (e.detail.fontSize) setFontSize(e.detail.fontSize);
                if (e.detail.readingFamily) setReadingFamily(e.detail.readingFamily);
            }
        };
        window.addEventListener('fontSettingsChanged', handleFontSettingsUpdate);
        
        // Signal that the post page is active
        window.isPostPageActive = true;
        window.dispatchEvent(new CustomEvent('postPageActiveChanged', { detail: { active: true } }));
        
        return () => {
            window.removeEventListener('fontSettingsChanged', handleFontSettingsUpdate);
            window.isPostPageActive = false;
            window.dispatchEvent(new CustomEvent('postPageActiveChanged', { detail: { active: false } }));
        };
    }, []);

    // ========== VIEWED DETECTION START ==========
    useEffect(() => {
        if (!get_slug) return;

        const markAsViewed = async (slug) => {
            // Guest logic (local storage)
            try {
                const localRead = JSON.parse(localStorage.getItem("readArticles") || "[]");
                if (localRead.includes(slug)) return; // Already read

                const localViewed = JSON.parse(localStorage.getItem("viewedArticles") || "[]");
                if (!localViewed.includes(slug)) {
                    localViewed.push(slug);
                    localStorage.setItem("viewedArticles", JSON.stringify(localViewed));
                    window.dispatchEvent(new Event('readArticlesUpdated'));
                }
            } catch (err) { console.error(err); }

            // Logged in logic
            const sessionUser = sessionStorage.getItem("user");
            if (sessionUser) {
                try {
                    const user = JSON.parse(sessionUser);
                    if (user && user.token) {
                        await fetch(API_ENDPOINTS.USER.VIEWED_ARTICLES, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${user.token}`
                            },
                            body: JSON.stringify({ slug })
                        });
                    }
                } catch (err) { console.error(err); }
            }
        };

        markAsViewed(get_slug);
    }, [get_slug]);
    // ========== VIEWED DETECTION END ==========

    // ========== SCROLL TO LAST HEADING DETECTION START ==========
    useEffect(() => {
        if (!post || !get_slug) return;

        const markAsRead = async (slug) => {
            // Guest logic (local storage) - always do this for immediate feedback
            try {
                const localRead = JSON.parse(localStorage.getItem("readArticles") || "[]");
                if (!localRead.includes(slug)) {
                    localRead.push(slug);
                    localStorage.setItem("readArticles", JSON.stringify(localRead));
                    
                    // Remove from viewed locally
                    const localViewed = JSON.parse(localStorage.getItem("viewedArticles") || "[]");
                    const updatedViewed = localViewed.filter(s => s !== slug);
                    localStorage.setItem("viewedArticles", JSON.stringify(updatedViewed));

                    // Dispatch event for Blog homepage to update UI
                    window.dispatchEvent(new Event('readArticlesUpdated'));
                }
            } catch (err) {
                console.error("Error updating local readArticles:", err);
            }

            // Logged in user logic (backend)
            const sessionUser = sessionStorage.getItem("user");
            if (sessionUser) {
                try {
                    const user = JSON.parse(sessionUser);
                    if (user && user.token) {
                        await fetch(API_ENDPOINTS.USER.READ_ARTICLES, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${user.token}`
                            },
                            body: JSON.stringify({ slug })
                        });
                    }
                } catch (err) {
                    console.error("Error marking article as read (server):", err);
                }
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    markAsRead(get_slug);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        // Wait for content to stabilize
        const timer = setTimeout(() => {
            // Find all headings rendered by JsonContentRenderer (rt-h1, rt-h2, etc.)
            const headings = document.querySelectorAll('.rt-h1, .rt-h2, .rt-h3, .rt-h4, .rt-h5, .rt-h6, h1, h2, h3, h4, h5, h6');
            if (headings.length > 0) {
                observer.observe(headings[headings.length - 1]);
            } else {
                // Fallback: observe the end of the rich text component
                const contentArea = document.querySelector('.rich-text-comp');
                if (contentArea && contentArea.lastElementChild) {
                    observer.observe(contentArea.lastElementChild);
                }
            }
        }, 3000);

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [post, get_slug]);
    // ========== SCROLL TO LAST HEADING DETECTION END ==========

    // Handle TOC visibility changes
    const handleTocVisibilityChange = useCallback((visible) => {
        if (tocVisibleRef.current !== visible) {
            tocVisibleRef.current = visible;
            setTocVisible(visible);
        }
    }, []);

    // Update body class based on TOC visibility
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (tocVisible) {
                document.body.classList.add('post-page-toc-visible');
            } else {
                document.body.classList.remove('post-page-toc-visible');
            }
        }, 200);

        return () => {
            clearTimeout(timeoutId);
            document.body.classList.remove('post-page-toc-visible');
        };
    }, [tocVisible]);

    // Sync Reading Family to Body for global background (e.g. behind footer)
    useEffect(() => {
        if (readingFamily && readingFamily !== 'standard') {
            document.body.dataset.readingFamily = readingFamily;
        } else {
            delete document.body.dataset.readingFamily;
        }
        return () => {
            delete document.body.dataset.readingFamily;
        };
    }, [readingFamily]);

    // Handle author click
    const handleAuthorClick = (authors, index = 0) => {
        setSelectedAuthors(authors);
        setSelectedAuthorIndex(index);
        setIsPopupOpen(true);
    };

    // Handle popup close
    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setSelectedAuthors([]);
        setSelectedAuthorIndex(0);
    };

    // Fetch comment count
    useEffect(() => {
        if (post && post._id) {
            fetch(API_ENDPOINTS.BLOG.THOUGHTS_COUNT(post._id))
                .then(res => res.json())
                .then(data => setCommentCount(data.count || 0))
                .catch(() => setCommentCount(0));
        }
    }, [post]);

    // Extract post data
    const title = post?.title || "Untitled Post";
    const description = post?.description || "";
    const slug = post?.slug || "";
    // Handle publishedAt and createdAt - convert to ISO string if Date object or ensure valid date string
    let publishedAt = "";
    if (post?.publishedAt) {
        try {
            const date = new Date(post.publishedAt);
            if (!isNaN(date.getTime())) {
                publishedAt = date.toISOString();
            }
        } catch (e) {
            publishedAt = typeof post.publishedAt === 'string' ? post.publishedAt : "";
        }
    }
    let createdAt = "";
    if (post?.createdAt) {
        try {
            const date = new Date(post.createdAt);
            if (!isNaN(date.getTime())) {
                createdAt = date.toISOString();
            }
        } catch (e) {
            // If conversion fails, try to use as-is if it's already a string
            createdAt = typeof post.createdAt === 'string' ? post.createdAt : "";
        }
    }
    const postId = post?._id || "";
    const rawCoverUrl = (post?.coverImage && post?.coverImage?.url) || "";
    const defaultThumbnail = process.env.REACT_APP_BLOG_DEFAULT_THUMBNAIL || "https://blog-post-details.s3.eu-north-1.amazonaws.com/Blog+Default+Thumbnail.jpg";
    const coverImageUrl = rawCoverUrl 
        ? (rawCoverUrl.startsWith('http') ? rawCoverUrl : `https://www.harivishnu.in${rawCoverUrl.startsWith('/') ? '' : '/'}${rawCoverUrl}`) 
        : defaultThumbnail;
    const thumbnailCopyright = post?.thumbnailCopyright || "";
    const updatedAt = post?.updatedAt || post?.createdAt || "";
    const keywords = post?.keywords || post?.tags || null;
    
    // Handle author data
    let authorData = [];
    if (post?.author) {
        // Handle array of authors (new format) or single author object (legacy)
        const authorsList = Array.isArray(post.author) ? post.author : [post.author];
        
        authorsList.forEach((author, idx) => {
             const authorImage = typeof author.image === 'string' 
                ? author.image 
                : ((author.image && author.image.url) || "/images/home_images/Logo.png");
            
            authorData.push({
                name: author.name || "Hari Vishnu",
                image: authorImage,
                bio: author.bio || `Meet ${author.name || "Hari Vishnu"}, a passionate writer and contributor to our blog.`,
                socialLinks: Array.isArray(author.socialLinks) ? author.socialLinks : (author.socialLinks ? [author.socialLinks] : []),
                index: idx
            });
        });
    } else {
        authorData.push({
            name: "Hari Vishnu",
            image: "/images/home_images/Logo.png",
            bio: "Meet Hari Vishnu, a passionate writer and contributor to our blog.",
            socialLinks: [],
            index: 0
        });
    }

    const authorNames = authorData.map(a => a.name).join(", ");

    // Handle category - ensure we get the slug properly
    const category = post?.category || {};
    // Handle both object format and direct property access
    const categorySlug = (category && category.slug) 
        ? category.slug 
        : (typeof category === 'object' && category.name 
            ? category.name.toLowerCase().replace(/\s+/g, '-') 
            : 'uncategorized');
    const categoryName = (category && category.name) || 'Uncategorized';

    // Calculate reading time
    const { readingTime } = getPostMetrics(post || {});

    // Helper function to convert TipTap node to RichText format (for TOC extraction)
    const convertTipTapNodeForToc = (tiptapNode) => {
        if (!tiptapNode || typeof tiptapNode !== 'object') return null;
        
        const { type, attrs = {}, content = [] } = tiptapNode;
        
        // Handle heading nodes
        if (type === 'heading' && attrs.level) {
            const headingTypes = ['heading-one', 'heading-two', 'heading-three', 'heading-four', 'heading-five', 'heading-six'];
            const headingType = headingTypes[attrs.level - 1] || 'heading-one';
            
            // Extract text from content
            const extractText = (nodes) => {
                if (!Array.isArray(nodes)) return [];
                return nodes.map(node => {
                    if (node.text) {
                        return { type: 'text', text: node.text };
                    }
                    if (node.content) {
                        return extractText(node.content);
                    }
                    return null;
                }).filter(Boolean).flat();
            };
            
            const children = extractText(content);
            if (children.length > 0) {
                return {
                    type: headingType,
                    children: children
                };
            }
        }
        
        // For other node types, we don't need them for TOC, so return null
        return null;
    };
    
    // Create a stable content hash to detect actual content changes (not just object reference changes)
    const contentHash = useMemo(() => {
        try {
            if (post?.content && typeof post?.content === 'object' && post?.content?.json) {
                return JSON.stringify(post.content.json);
            }
            return String(post?.content || '');
        } catch {
            return String(post?.content || '');
        }
    }, [post?.content]);

    // Convert TipTap to RichText format for TOC extraction - MEMOIZED with stable dependency
    const richTextContentForToc = useMemo(() => {
        if (!post?.content) {
            return null;
        }
        
        let tiptapContent = null;
        
        if (typeof post.content === 'object' && post.content.json) {
            tiptapContent = typeof post.content.json === 'string' 
                ? JSON.parse(post.content.json) 
                : post.content.json;
        } else if (typeof post.content === 'object' && post.content.type === 'doc') {
            tiptapContent = post.content;
        } else if (typeof post.content === 'string') {
            try {
                tiptapContent = JSON.parse(post.content);
            } catch (e) {
                return null;
            }
        } else if (typeof post.content === 'object' && Array.isArray(post.content.content)) {
            tiptapContent = post.content;
        } else if (Array.isArray(post.content)) {
            tiptapContent = { type: 'doc', content: post.content };
        }
        
        if (!tiptapContent) return null;
        
        // If it's a TipTap doc structure, convert it
        if (tiptapContent.type === 'doc' && Array.isArray(tiptapContent.content)) {
            const children = tiptapContent.content
                .map(node => convertTipTapNodeForToc(node))
                .filter(Boolean);
            return { children: children };
        } else if (Array.isArray(tiptapContent.content)) {
            const children = tiptapContent.content
                .map(node => convertTipTapNodeForToc(node))
                .filter(Boolean);
            return { children: children };
        } else if (tiptapContent.children && Array.isArray(tiptapContent.children)) {
            // Already in RichText format
            return tiptapContent;
        }
        
        return null;
    }, [contentHash]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Extract headings from converted RichText format - MEMOIZED
    const headings = useMemo(() => {
        return richTextContentForToc 
            ? extractHeadings(richTextContentForToc) 
            : [];
    }, [richTextContentForToc]);

    // JSON-LD structured data (BlogPosting + GEO-optimised)
    const postUrl = `https://www.harivishnu.in/blog/${categorySlug}/${slug}`;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": title,
        "description": description,
        "image": coverImageUrl || "https://www.harivishnu.in/images/home_images/Logo.png",
        "url": postUrl,
        "datePublished": createdAt,
        "dateModified": updatedAt || createdAt,
        "inLanguage": "en-IN",
        "author": {
            "@type": "Person",
            "name": authorNames || "Hari Vishnu",
            "image": (authorData[0] && authorData[0].image) || "https://www.harivishnu.in/images/home_images/Logo.png",
            "url": "https://www.harivishnu.in/contact"
        },
        "publisher": {
            "@type": "Person",
            "name": "Hari Vishnu",
            "url": "https://www.harivishnu.in",
            "logo": {
                "@type": "ImageObject",
                "url": "https://www.harivishnu.in/images/home_images/Logo_Transparent.png"
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": postUrl
        },
        "isPartOf": {
            "@type": "Blog",
            "name": "Hari Vishnu Blog",
            "url": "https://www.harivishnu.in/blog"
        },
        ...(keywords ? { "keywords": typeof keywords === 'string' ? keywords : keywords.join(', ') } : {}),
    };

    const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.harivishnu.in/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.harivishnu.in/blog" },
            { "@type": "ListItem", "position": 3, "name": categoryName || "Uncategorized", "item": `https://www.harivishnu.in/blog/${categorySlug}` },
            { "@type": "ListItem", "position": 4, "name": title, "item": postUrl }
        ]
    };

    // eslint-disable-next-line no-unused-vars
    const normalizeContentForRichText = (content) => {
        // If content is already in the correct format
        if (content && typeof content === 'object') {
            // If it has children array, it's already correct
            if (Array.isArray(content.children) || Array.isArray(content)) {
                return Array.isArray(content) ? { children: content } : content;
            }
            // If it's a JSON string, parse it
            if (typeof content === 'string') {
                try {
                    const parsed = JSON.parse(content);
                    return normalizeContentForRichText(parsed);
                } catch (e) {
                    return null;
                }
            }
        }
        return null;
    };

    // Convert TipTap format to GraphCMS RichText format
    const convertTipTapToRichText = (tiptapNode) => {
        if (!tiptapNode || typeof tiptapNode !== 'object') {
            return null;
        }

        const type = tiptapNode.type || '';
        const attrs = tiptapNode.attrs || {};
        const content = tiptapNode.content || [];
        const marks = tiptapNode.marks || [];
        const text = tiptapNode.text || '';

        // Handle text nodes
        if (type === 'text') {
            // If text has link mark, create a link node instead of text node
            const linkMark = marks.find(m => m && m.type === 'link');
            if (linkMark && linkMark.attrs) {
                const linkNode = {
                    type: 'link',
                    href: linkMark.attrs.href || '#',
                    openInNewTab: linkMark.attrs.target === '_blank' || false,
                    children: [{
                        text: text,
                        bold: marks.some(m => m && m.type === 'bold'),
                        italic: marks.some(m => m && m.type === 'italic'),
                        underline: marks.some(m => m && m.type === 'underline'),
                        code: marks.some(m => m && m.type === 'code')
                    }]
                };
                return linkNode;
            }
            
            // Regular text node with marks as properties
            const textNode = {
                text: text
            };
            
            // Add marks as properties (GraphCMS RichText format)
            marks.forEach(mark => {
                if (mark && mark.type) {
                    if (mark.type === 'bold') textNode.bold = true;
                    if (mark.type === 'italic') textNode.italic = true;
                    if (mark.type === 'underline') textNode.underline = true;
                    if (mark.type === 'code') textNode.code = true;
                }
            });
            
            return textNode;
        }

        // Handle block nodes - convert to GraphCMS RichText format
        const children = content.map(child => convertTipTapToRichText(child)).filter(Boolean);

        switch (type) {
            case 'paragraph':
                // Check if paragraph contains only an image - if so, return the image directly
                // Otherwise, return paragraph with children
                if (children.length === 1 && children[0] && children[0].type === 'image') {
                    // If paragraph contains only an image, return the image node directly
                    return children[0];
                }
                return {
                    type: 'paragraph',
                    children: children.length > 0 ? children : [{ text: '' }]
                };
            case 'heading':
                const level = attrs.level || 1;
                // RichText uses heading-one, heading-two, etc.
                const headingTypes = ['heading-one', 'heading-two', 'heading-three', 'heading-four', 'heading-five', 'heading-six'];
                return {
                    type: headingTypes[level - 1] || 'heading-one',
                    children: children
                };
            case 'bulletList':
                return {
                    type: 'bulleted-list',
                    children: children
                };
            case 'orderedList':
                return {
                    type: 'numbered-list',
                    children: children
                };
            case 'listItem':
                // List items in RichText should contain paragraphs or text directly
                // If children contains paragraphs, keep them; otherwise wrap in paragraph
                let listItemChildren = children;
                
                // If no children, add empty paragraph
                if (listItemChildren.length === 0) {
                    listItemChildren = [{ type: 'paragraph', children: [{ text: '' }] }];
                } else {
                    // Check if children are already paragraphs - if not, wrap them
                    const hasParagraphs = listItemChildren.some(child => child.type === 'paragraph');
                    if (!hasParagraphs) {
                        // Wrap all children in a paragraph
                        listItemChildren = [{
                            type: 'paragraph',
                            children: listItemChildren
                        }];
                    }
                }
                
                return {
                    type: 'list-item',
                    children: listItemChildren
                };
            case 'blockquote':
                return {
                    type: 'quote',
                    children: children
                };
            case 'codeBlock':
                return {
                    type: 'code',
                    children: children
                };
            case 'hardBreak':
                return {
                    text: '\n'
                };
            case 'table':
                try {
                    // Convert TipTap table to RichText table format
                    // TipTap table structure: table -> tableRow -> (tableHeader | tableCell)
                    // RichText table structure: table -> (thead | tbody) -> tr -> table_cell
                    console.log('🔍 [Table Debug] Converting table node:', JSON.stringify(tiptapNode, null, 2));
                    console.log('🔍 [Table Debug] Table content array length:', content.length);
                    
                    // Process rows - convert each row first
                    const tableRows = [];
                    let firstRowIsHeader = false;
                    
                    for (let i = 0; i < content.length; i++) {
                        const row = content[i];
                        console.log(`🔍 [Table Debug] Processing row ${i}:`, row);
                        try {
                            const convertedRow = convertTipTapToRichText(row);
                            console.log(`🔍 [Table Debug] Converted row ${i}:`, convertedRow);
                            if (convertedRow && convertedRow.type === 'tr') {
                                // Check if this is the first row and contains headers
                                if (i === 0 && convertedRow.isHeaderRow) {
                                    firstRowIsHeader = true;
                                }
                                tableRows.push(convertedRow);
                            } else {
                                console.warn(`🔍 [Table Debug] Row ${i} conversion failed or returned wrong type:`, convertedRow);
                            }
                        } catch (rowError) {
                            console.error(`🔍 [Table Debug] Error converting row ${i}:`, rowError, row);
                        }
                    }
                    
                    console.log('🔍 [Table Debug] Total converted rows:', tableRows.length);
                    console.log('🔍 [Table Debug] First row is header:', firstRowIsHeader);
                    
                    // If no rows were converted, return null to avoid empty table
                    if (tableRows.length === 0) {
                        console.error('🔍 [Table Debug] ❌ ERROR: No rows were successfully converted!');
                        return null;
                    }
                    
                    // Build table structure - GraphCMS RichText might not support thead/tbody
                    // So we'll put all rows directly under table, but mark header rows
                    // The renderer will handle wrapping them in thead/tbody
                    const convertedTable = {
                        type: 'table',
                        children: tableRows
                    };
                    
                    console.log('🔍 [Table Debug] ✅ Final converted table:', JSON.stringify(convertedTable, null, 2));
                    
                    return convertedTable;
                } catch (error) {
                    console.error('🔍 [Table Debug] ❌ ERROR converting table:', error, tiptapNode);
                    return null;
                }
            case 'tableRow':
                // Convert TipTap tableRow to RichText tr format
                console.log('ðŸ” [Table Debug] TableRow content array length:', content.length);
                
                const rowChildren = [];
                let cellsWithContent = 0;
                let cellsWithBold = 0;
                
                for (let i = 0; i < content.length; i++) {
                    const cell = content[i];
                    console.log(`ðŸ” [Table Debug] Processing cell ${i} in row:`, cell);
                    
                    // Check if cell has any content
                    const hasContent = cell && cell.content && cell.content.length > 0;
                    if (hasContent) {
                        cellsWithContent++;
                    }
                    
                    // Check if cell has bold text (indicator of header row)
                    const hasBoldText = cell && cell.content && cell.content.some(para => 
                        para && para.content && para.content.some(textNode => 
                            textNode && textNode.marks && textNode.marks.some(mark => mark.type === 'bold')
                        )
                    );
                    
                    if (hasBoldText) {
                        cellsWithBold++;
                    }
                    
                    const convertedCell = convertTipTapToRichText(cell);
                    console.log(`ðŸ” [Table Debug] Converted cell ${i}:`, convertedCell);
                    if (convertedCell) {
                        rowChildren.push(convertedCell);
                    }
                }
                
                // Check if this row contains headers by checking:
                // 1. If cells are tableHeader type
                // 2. If cells have isHeader attribute
                // 3. If all cells with content have bold text (common header pattern)
                const hasHeaderType = content.some(cell => 
                    cell && cell.type === 'tableHeader'
                );
                const hasHeaderAttr = content.some(cell => 
                    cell && cell.attrs && cell.attrs.isHeader === true
                );
                
                // If all cells with content have bold text, treat as header row
                // Also check if at least some cells have bold (for tables with mixed content)
                const isHeaderByBold = cellsWithContent > 0 && cellsWithBold === cellsWithContent;
                
                console.log('ðŸ” [Table Debug] Cells with content:', cellsWithContent);
                console.log('ðŸ” [Table Debug] Cells with bold:', cellsWithBold);
                
                const hasHeaders = hasHeaderType || hasHeaderAttr || isHeaderByBold;
                
                console.log('ðŸ” [Table Debug] Row has headers (type):', hasHeaderType);
                console.log('ðŸ” [Table Debug] Row has headers (attr):', hasHeaderAttr);
                console.log('ðŸ” [Table Debug] Row has headers (bold):', isHeaderByBold);
                console.log('ðŸ” [Table Debug] Row has headers (final):', hasHeaders);
                console.log('ðŸ” [Table Debug] Row children count:', rowChildren.length);
                
                // If this is a header row, convert all cells to header type
                if (hasHeaders && rowChildren.length > 0) {
                    rowChildren.forEach(cell => {
                        if (cell && cell.type === 'table_cell') {
                            cell.tableCellType = 'header';
                        }
                    });
                }
                
                const convertedRow = {
                    type: 'tr',
                    isHeaderRow: hasHeaders,
                    children: rowChildren
                };
                
                console.log('ðŸ” [Table Debug] Final converted row:', JSON.stringify(convertedRow, null, 2));
                
                return convertedRow;
            case 'tableHeader':
                // Convert TipTap tableHeader to RichText table_cell with header type
                console.log('ðŸ” [Table Debug] Converting tableHeader node:', tiptapNode);
                const headerCellChildren = content.map(child => convertTipTapToRichText(child)).filter(Boolean);
                // Flatten paragraph children if cell contains only paragraphs
                let headerContent = [];
                if (headerCellChildren.length === 0) {
                    headerContent = [{ text: '' }];
                } else if (headerCellChildren.length === 1 && headerCellChildren[0].type === 'paragraph') {
                    // If cell contains only one paragraph, use its children directly
                    headerContent = headerCellChildren[0].children || [{ text: '' }];
                } else {
                    // Multiple children or non-paragraph content
                    headerContent = headerCellChildren;
                }
                return {
                    type: 'table_cell',
                    tableCellType: 'header',
                    children: headerContent
                };
            case 'tableCell':
                // Convert TipTap tableCell to RichText table_cell format
                console.log('ðŸ” [Table Debug] Converting tableCell node:', tiptapNode, 'isHeader:', attrs.isHeader);
                const cellChildren = content.map(child => convertTipTapToRichText(child)).filter(Boolean);
                // Flatten paragraph children if cell contains only paragraphs
                let cellContent = [];
                if (cellChildren.length === 0) {
                    cellContent = [{ text: '' }];
                } else if (cellChildren.length === 1 && cellChildren[0].type === 'paragraph') {
                    // If cell contains only one paragraph, use its children directly
                    cellContent = cellChildren[0].children || [{ text: '' }];
                } else {
                    // Multiple children or non-paragraph content
                    cellContent = cellChildren;
                }
                return {
                    type: 'table_cell',
                    tableCellType: attrs.isHeader ? 'header' : 'body',
                    children: cellContent
                };
            case 'image':
            case 'imageWithTitle':
                // Try multiple possible attribute names for image source
                let src = attrs.src || attrs.url || attrs.href || '';
                
                // If src is an object, try to extract URL from it
                if (!src && typeof attrs === 'object') {
                    if (attrs.src && typeof attrs.src === 'object' && attrs.src.url) {
                        src = attrs.src.url;
                    } else if (attrs.url && typeof attrs.url === 'object' && attrs.url.url) {
                        src = attrs.url.url;
                    } else if (attrs.src && typeof attrs.src === 'object' && attrs.src.src) {
                        src = attrs.src.src;
                    }
                }
                
                // Check if the node itself has a url property
                if (!src && tiptapNode.url) {
                    src = tiptapNode.url;
                }
                
                // Check if src is nested in attrs
                if (!src && attrs && typeof attrs === 'object') {
                    // Try to find any string that looks like a URL
                    for (const key in attrs) {
                        const value = attrs[key];
                        if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('/') || value.startsWith('data:'))) {
                            src = value;
                            break;
                        }
                    }
                }
                
                const alt = attrs.alt || attrs.imageTitle || attrs.title || '';
                
                
                if (src && src.trim()) {
                    // Convert to paragraph with image data that can be rendered
                    // GraphCMS RichText doesn't support standalone image nodes
                    // So we wrap it in a paragraph with special data
                    const imageNode = {
                        type: 'paragraph',
                        children: [{
                            type: 'text',
                            text: '',
                            // Store image data in a way that can be detected
                            // We'll use a special marker in the text and data attributes
                            imageData: {
                        src: src.trim(),
                        alt: alt || 'post-image',
                        title: attrs.title || attrs.imageTitle || alt || 'post-image'
                            }
                        }],
                        // Add image data at paragraph level for easier access
                        imageData: {
                            src: src.trim(),
                            alt: alt || 'post-image',
                            title: attrs.title || attrs.imageTitle || alt || 'post-image'
                        }
                    };
                    return imageNode;
                }
                return null;
            case 'twitterEmbed':
                // Convert to standard paragraph. This is the safest, most compatible way.
                // The p renderer logic will catch the specific marker text.
                if (attrs && attrs.url) {

                    return {
                        type: 'paragraph',
                        children: [{
                            text: `[TWEET_EMBED: ${attrs.url}]`
                        }]
                    };
                }
                return null;
            default:
                // For unknown types, log and try to process as paragraph
                if (type && type !== 'doc') {

                }
                // For unknown types, try to process as paragraph
                if (children.length > 0) {
                    return {
                        type: 'paragraph',
                        children: children
                    };
                }
                return null;
        }
    };

    // Memoize the converted RichText content - CRITICAL: Use post._id for stable dependency
    // This ensures conversion runs ONLY ONCE per post, not on every render
    const convertedRichTextContent = useMemo(() => {
        if (!post?.content) {
            return null;
        }

        // Extract JSON content from MongoDB post
        let tiptapContent = null;
        
        if (typeof post.content === 'object' && post.content.json) {
            // If content has json property
            tiptapContent = typeof post.content.json === 'string' 
                ? JSON.parse(post.content.json) 
                : post.content.json;
        } else if (typeof post.content === 'object' && post.content.type === 'doc') {
            // If content is already TipTap doc format
            tiptapContent = post.content;
        } else if (typeof post.content === 'string') {
            // If content is a JSON string
            try {
                tiptapContent = JSON.parse(post.content);
            } catch (e) {

                return null;
            }
        }

        // Check for tables in the content
        if (tiptapContent && tiptapContent.content) {
            const hasTable = tiptapContent.content.some(node => 
                node.type === 'table' || 
                (node.content && node.content.some(child => child.type === 'table'))
            );
            if (hasTable) {

            }
        }

        // Convert TipTap format to GraphCMS RichText format
        if (!tiptapContent) {
            return null;
        }

        let richTextContent = null;
        
        // If it's a TipTap doc structure
        if (tiptapContent.type === 'doc' && Array.isArray(tiptapContent.content)) {
            // Log all nodes before conversion to see image nodes
            const imageNodes = tiptapContent.content.filter(node => 
                node && (node.type === 'image' || node.type === 'imageWithTitle')
            );
            if (imageNodes.length > 0) {
            }
            
            const children = tiptapContent.content
                .map((node, index) => {

                    const converted = convertTipTapToRichText(node);
                    if (!converted && node.type === 'table') {

                    }
                    return converted;
                })
                .filter(Boolean);
            
            // Log converted table nodes
            const convertedTables = children.filter(node => 
                node && node.type === 'table'
            );
            if (convertedTables.length > 0) {
            } else {

                // Check if any nodes were tables before conversion
                const originalTables = tiptapContent.content.filter(node => node.type === 'table');
                if (originalTables.length > 0) {

                }
            }
            
            // Log converted image nodes (now paragraphs with imageData)
            // eslint-disable-next-line no-unused-vars
            const convertedImages = children.filter(node => 
                node && node.type === 'paragraph' && node.imageData
            );
            richTextContent = { children: children };
        } else if (Array.isArray(tiptapContent)) {
            // If it's already an array of nodes
            const children = tiptapContent
                .map(node => convertTipTapToRichText(node))
                .filter(Boolean);
            richTextContent = { children: children };
        } else if (tiptapContent.children && Array.isArray(tiptapContent.children)) {
            // Already in RichText format
            richTextContent = tiptapContent;
        }

        return richTextContent;
    }, [post?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Memoize image grouping logic - CRITICAL: Use post._id for stable dependency
    const imageGroupingData = useMemo(() => {
        if (!convertedRichTextContent || !convertedRichTextContent.children || convertedRichTextContent.children.length === 0) {
            return { mixedChildren: null, imageParagraphs: [], imageGroups: [] };
        }

        
        // Pre-process: Extract image paragraphs and group consecutive ones
        const mixedChildren = [];
        const imageDataMap = new Map(); // Map index to imageData
        const imageGroups = []; // Groups of consecutive images
        let currentImageGroup = [];
        let lastWasImage = false;
        
        convertedRichTextContent.children.forEach((child, index) => {
            if (child && child.type === 'paragraph' && child.imageData) {
                // This is an image paragraph
                imageDataMap.set(index, child.imageData);
                
                // Check if this is consecutive to previous image
                if (lastWasImage) {
                    // Add to current group
                    currentImageGroup.push({ index, imageData: child.imageData });
                } else {
                    // Start new group
                    if (currentImageGroup.length > 0) {
                        imageGroups.push([...currentImageGroup]);
                    }
                    currentImageGroup = [{ index, imageData: child.imageData }];
                }
                lastWasImage = true;
                
                // Add a placeholder paragraph that we can replace
                mixedChildren.push({
                    type: 'paragraph',
                    children: [{ type: 'text', text: `__IMAGE_PLACEHOLDER_${index}__` }]
                });
            } else {
                // Not an image - close current group if exists
                if (currentImageGroup.length > 0) {
                    imageGroups.push([...currentImageGroup]);
                    currentImageGroup = [];
                }
                lastWasImage = false;
                mixedChildren.push(child);
            }
        });
        
        // Don't forget the last group
        if (currentImageGroup.length > 0) {
            imageGroups.push([...currentImageGroup]);
        }
        
        // Convert imageDataMap to array format
        const imageParagraphs = Array.from(imageDataMap.entries()).map(([index, imageData]) => ({
            index,
            imageData
        }));


        return {
            mixedChildren: imageDataMap.size > 0 ? mixedChildren : null,
            imageParagraphs,
            imageGroups
        };
    }, [convertedRichTextContent]); // Depends on convertedRichTextContent, which is stable due to post._id memoization

    // Memoize all props for GroupedRichText to prevent unnecessary re-renders
    // CRITICAL: These must be memoized to prevent React.memo from seeing "new" props on every render
    // eslint-disable-next-line no-unused-vars
    const stableContent = useMemo(() => {
        if (!convertedRichTextContent) return null;
        const { mixedChildren } = imageGroupingData;
        if (mixedChildren) {
            return { children: mixedChildren };
        }
        return convertedRichTextContent;
    }, [convertedRichTextContent, imageGroupingData]);

    // eslint-disable-next-line no-unused-vars
    const stableReferences = useMemo(() => {
        const refs = post?.content && post?.content?.references;
        // Return the actual references array if it exists and is non-empty
        // Otherwise return constant empty array (same reference every time)
        if (refs && Array.isArray(refs) && refs.length > 0) {
            return refs;
        }
        // Return constant empty array - same reference every time, prevents re-renders
        return EMPTY_REFERENCES;
    }, [post?.content && post?.content?.references]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line no-unused-vars
    const stableImageParagraphs = useMemo(() => {
        return imageGroupingData.imageParagraphs;
    }, [imageGroupingData]);

    // eslint-disable-next-line no-unused-vars
    const stableImageGroups = useMemo(() => {
        return imageGroupingData.imageGroups;
    }, [imageGroupingData]);

    if (!post) return null;

    // Simplified Content Rendering using JsonContentRenderer
    // This bypasses the old conversion logic and renders TipTap JSON directly
    const renderContent = () => {
        if (!post.content) {
            return null;
        }

        const contentJson = post.content.json || post.content;

        return (
            <article id="blog-content" className="mongodb-post-content" data-font-size={fontSize}>
                <JsonContentRenderer content={contentJson} />
            </article>
        );
    };

    return (
        <>
            <div className="all-blog-area mongodb-all-blog-area mx-auto flex w-full max-w-[1700px] flex-col px-[16px] text-text" data-reading-family={readingFamily} style={{ flexDirection: 'column' }}>
                {/* Breadcrumbs - Wrapped to take full width in flex container */}
                <div style={{ width: '100%' }}>
                    <Breadcrumbs hideLastItem={true} />
                </div>
                
                <Helmet>

                    <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large" />
                    
                    <script type="application/ld+json">
                        {JSON.stringify(jsonLd)}
                    </script>
                    <script type="application/ld+json">
                        {JSON.stringify(breadcrumbLd)}
                    </script>
                </Helmet>

                <div className="all-blog-area w-full items-start gap-[1.5rem]" style={{ width: '100%', margin: '0 auto' }}>
                    <div key={`mongodb-post-${postId}`} className="mongodb-post-area w-full max-w-none" id="mongodb-post-area">


                        <h1 className="mongodb-title m-0 text-[clamp(1.8rem,4vw,2.8rem)] font-[800] leading-[1.2] text-text">{title}</h1>

                        <p className="mongodb-excerpt mt-[0.75rem] text-[1.02rem] leading-[1.7] text-[#8f8f8f]">{description}</p>

                        <SEO 
                            title={title}
                            description={description}
                            image={coverImageUrl}
                            url={`https://www.harivishnu.in/blog/${categorySlug}/${slug}`}
                            type="article"
                            author={authorNames}
                            publishedTime={createdAt}
                            seoTitle={post.seoTitle}
                            seoDescription={post.seoDescription}
                            seoImage={post.seoImage}
                            keywords={(post.seoKeywords && post.seoKeywords.join(', ')) || ''}
                        />

                        {/* Post meta container */}
                        <div className="mongodb-post-meta-container mt-[1rem]">
                            {/* Author and share row - Moved above cover image */}
                            <div className="mongodb-post-meta-row flex flex-wrap items-center justify-between gap-[12px]">
                                {/* Left side: Author info */}
                                <div className="mongodb-post-meta-left flex min-w-0 flex-1 items-center gap-[12px]">
                                    {/* Big author image - Overlapping for multiple authors */}
                                    <div className="mongodb-author-image-large">
                                        {authorData.length > 0 && (
                                            <div className={`mongodb-author-images-container ${authorData.length > 1 ? 'overlapped' : ''}`}>
                                                {authorData.map((author, idx) => (
                                                    <img 
                                                        key={idx}
                                                        className="mongodb-author-img-large mongodb-author-clickable" 
                                                        src={author.image} 
                                                        alt={`${author.name} profile`}
                                                        title={`Click to view ${author.name}'s profile`}
                                                        onClick={() => handleAuthorClick(authorData, idx)}
                                                        onError={(e) => {
                                                            e.target.src = "/images/home_images/Logo.png";
                                                        }}
                                                        style={authorData.length > 1 ? {
                                                            zIndex: authorData.length - idx,
                                                            marginLeft: idx > 0 ? '-25px' : '0'
                                                        } : {}}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {/* Author name and metadata */}
                                    <div className="mongodb-author-meta-info min-w-0">
                                        <span className="mongodb-author-name-large">
                                            {authorData.length > 1 ? (
                                                <span 
                                                    className="mongodb-author-name mongodb-author-clickable"
                                                    onClick={() => handleAuthorClick(authorData, 0)}
                                                    title="Click to view all authors"
                                                >
                                                    Authors
                                                </span>
                                            ) : (
                                                authorData.map((author, index) => (
                                                    <span 
                                                        key={index}
                                                        className="mongodb-author-name mongodb-author-clickable"
                                                        onClick={() => handleAuthorClick(authorData, index)}
                                                        title={`Click to view ${author.name}'s profile`}
                                                    >
                                                        {author.name}
                                                    </span>
                                                ))
                                            )}
                                        </span>
                                        <div className="mongodb-author-meta-details mt-[4px] flex flex-wrap items-center gap-[8px] text-[0.86rem] text-[#8a8a8a]">
                                            <span className="mongodb-date-issue">{formatPublishedDateTime(publishedAt || createdAt) || formatRelativeOrDate(publishedAt || createdAt)}</span>
                                            {readingTime > 0 && (
                                                <>
                                                    <div className="mongodb-dot-container">
                                                        <span className="mongodb-dot"></span>
                                                    </div>
                                                    <span className="mongodb-post-reading-time">
                                                        <i className="fi fi-rr-clock"></i>
                                                        {readingTime} min read
                                                    </span>
                                                </>
                                            )}
                                            {commentCount > 0 && (
                                                <>
                                                    <div className="mongodb-dot-container">
                                                        <span className="mongodb-dot"></span>
                                                    </div>
                                                    <span className="mongodb-comments-count">
                                                        <i className="fi fi-rr-comment-alt"></i> 
                                                        <span className="mongodb-comment-count">{commentCount}</span>
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right side: Share section */}
                                <div className="mongodb-post-meta-right">
                                    <div className="mongodb-share-section flex items-center gap-[10px]">
                                        <div className="mongodb-share-text text-[0.82rem] font-[700] uppercase tracking-[0.08em] text-[#8a8a8a]">Share</div>
                                        <SharePost title={title} slug={slug} category={categorySlug} />
                                    </div>
                                </div>
                            </div>

                            {/* Listen to Article - Placed below meta row */}
                            {/* Cover image - Moved below meta row */}
                            {coverImageUrl && (
                                <>
                                    <img src={coverImageUrl} className="mongodb-cover-image mt-[14px] w-full rounded-[14px] object-cover" alt="Cover" />
                                    {thumbnailCopyright && (
                                        <p className="mongodb-thumbnail-copyright">
                                            {thumbnailCopyright}
                                        </p>
                                    )}
                                    <div className="post-settings-container mt-[12px] flex flex-wrap items-center justify-end gap-[10px]">
                                        <ListenToArticle />
                                        <FontSizeSettings 
                                            fontSize={fontSize} 
                                            onFontSizeChange={(size) => {
                                                setFontSize(size);
                                                sessionStorage.setItem('post-font-size', size);
                                                window.dispatchEvent(new CustomEvent('fontSettingsChanged', { detail: { fontSize: size, readingFamily } }));
                                            }} 
                                            readingFamily={readingFamily}
                                            onReadingFamilyChange={(family) => {
                                                setReadingFamily(family);
                                                sessionStorage.setItem('post-reading-family', family);
                                                window.dispatchEvent(new CustomEvent('fontSettingsChanged', { detail: { fontSize, readingFamily: family } }));
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        
                        <div className="mongodb-post-categories"></div>
                        <hr className="mongodb-hr-line my-[1rem] border-t border-slider"/>

                        {/* Table of Contents */}
                        {richTextContentForToc && (
                            <TableOfContent richTextJson={richTextContentForToc} />
                        )}

                        {/* Dynamic Table of Contents */}
                        {headings.length > 0 && (
                            <DynamicTableOfContents 
                                headings={headings} 
                                onVisibilityChange={handleTocVisibilityChange}
                            />
                        )}

                        <InlineBundleStyles />
                        {renderContent()}

                        {/* Author Popup - Passing full authors array */}
                        {isPopupOpen && selectedAuthors.length > 0 && (
                            <AuthorPopup 
                                authors={selectedAuthors}
                                initialAuthorIndex={selectedAuthorIndex}
                                isOpen={isPopupOpen} 
                                onClose={handlePopupClose} 
                            />
                        )}

                        {/* Photo Collection */}
                        {post.photoCollection && Array.isArray(post.photoCollection) && post.photoCollection.length > 0 && (
                            <div className="mongodb-image-post-area my-[2rem] rounded-[12px] border border-slider bg-bg p-[12px]">
                                <div className="mongodb-image-details">
                                    <LazyLoadImage
                                        className="mongodb-image-post-view" 
                                        src={(post.photoCollection[value] && post.photoCollection[value].url) || ""} 
                                        alt={`collection-image-${value+1}`}
                                        effect="blur"
                                    />

                                    <DashedLines totalDashes={post.photoCollection.length} currentDashIndex={value} />
                                    
                                    <p 
                                        className="mongodb-image-title"
                                        style={{display: (value+1) ? '' : 'none'}}>
                                        <strong>
                                            {value+1 ? value+1 : ""}<sup>{getOrdinalSuffix(value+1)}</sup> image
                                        </strong>: {(post.photoCollection[value] && post.photoCollection[value].imageTitle) || ""}
                                    </p>
                                </div>

                                <div className="mongodb-image-post-controls mt-[10px] flex items-center justify-between gap-[12px]">
                                    <button 
                                        className="mongodb-prev-post-btn"
                                        onClick={() => setValue(a => a-1)}
                                        disabled={value===0 ? true : ""}
                                    >
                                        <i className="fi fi-rr-caret-left"></i>
                                    </button>
                                    
                                    <p className="mongodb-image-post-number">{value+1} / {post.photoCollection.length}</p>

                                    <button 
                                        className="mongodb-next-post-btn"
                                        onClick={() => setValue(a => a+1)}
                                        disabled={value===post.photoCollection.length-1 ? true : ""}
                                    >
                                        <i className="fi fi-rr-caret-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}  

                        {/* Comments section */}
                        <div key={postId} className="mongodb-comments-section mt-[1.25rem]">
                            <BlogThoughts blogId={postId} />
                        </div>

                        {/* Continue Reading Section */}
                        <ContinueReading currentPostCategorySlug={categorySlug} />

                        <SidebarContentGroup
                            className="mongodb-releaventPost-and-ads mongodb-mobile-releaventPost-and-ads"
                            sections={["relatedTopics", "subscribe", "followMe", "ad"]}
                        />
                    </div>

                    <SidebarContentGroup
                        className="mongodb-releaventPost-and-ads mongodb-desktop-releaventPost-and-ads lg:my-[1.5rem] lg:mx-[1rem]"
                        sections={["ad", "relatedTopics", "subscribe", "followMe"]}
                    />
                </div>
                
                {/* Author Popup - Moved inside for theme inheritance */}
                {isPopupOpen && selectedAuthors.length > 0 && (
                    <AuthorPopup 
                        authors={selectedAuthors} 
                        initialAuthorIndex={selectedAuthorIndex}
                        isOpen={isPopupOpen} 
                        onClose={handlePopupClose} 
                    />
                )}
            </div>
        </>
    );
}
// ========== MONGODB POST RENDERER COMPONENT END ==========

// ========== DYNAMIC TABLE OF CONTENTS FEATURE START ==========

// Dynamic Table of Contents Component
function DynamicTableOfContents({ headings, onVisibilityChange }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentHeading, setCurrentHeading] = useState('');
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        // Check for dark mode
        const checkDarkMode = () => {
            setIsDarkMode(document.body.classList.contains('dark-mode'));
        };
        
        checkDarkMode();
        
        // Listen for dark mode changes
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        
        return () => observer.disconnect();
    }, []);

    // Notify parent component when visibility changes with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (onVisibilityChange) {
                onVisibilityChange(isVisible);
            }
        }, 150); // 150ms debounce

        return () => clearTimeout(timeoutId);
    }, [isVisible, onVisibilityChange]);

    useEffect(() => {
        if (!headings || headings.length === 0) return;

        // Cache DOM elements to avoid repeated queries that might trigger re-renders
        let cachedTocContainer = null;
        let cachedBlogContent = null;
        let cacheValid = false;

        const handleScroll = () => {
            try {
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const windowHeight = window.innerHeight || 0;
                const documentHeight = document.documentElement.scrollHeight || 0;
                
                // Cache DOM queries to avoid triggering re-renders
                // Re-check TOC container on each scroll in case it wasn't found initially
                if (!cachedTocContainer) {
                    // Find the actual TOC container (static TOC) - this is what we want to scroll past
                    cachedTocContainer = document.querySelector('.toc-container');
                }
                
                if (!cacheValid || !cachedBlogContent) {
                    cachedBlogContent = document.querySelector('.post-area, .mongodb-post-area');
                    cacheValid = true;
                }
                
                // Find the end of blog content (comments section or last element)
                let commentsSection = null;
                let mobileFollowups = null;
                let releaventPostAndAds = null;
                
                try {
                    if (!cacheValid) {
                        commentsSection = document.querySelector('.comments-section');
                        mobileFollowups = document.querySelector('.mobile-followups');
                        releaventPostAndAds = document.querySelector('.releaventPost-and-ads');
                    }
                } catch (e) {
                    // Ignore errors
                }
                
                // Get the last visible element in the blog content
                const lastElement = commentsSection || mobileFollowups || releaventPostAndAds || cachedBlogContent;
                
                let blogContentEnd = 0;
                if (lastElement) {
                    try {
                        blogContentEnd = (lastElement.offsetTop || 0) + (lastElement.offsetHeight || 0);
                    } catch (e) {
                        blogContentEnd = documentHeight - windowHeight;
                    }
                } else {
                    // Fallback: use document height minus viewport height
                    blogContentEnd = documentHeight - windowHeight;
                }
                
                // Calculate TOC container bottom position
                let tocContainerBottom = 0;
                if (cachedTocContainer) {
                    try {
                        const tocRect = cachedTocContainer.getBoundingClientRect();
                        tocContainerBottom = tocRect.bottom + scrollY;
                    } catch (e) {
                        // If TOC not found, fallback to first heading
                        const firstHeading = document.querySelector('.rt-h1, .rt-h2, .rt-h3, .mongodb-post-area .rt-h1, .mongodb-post-area .rt-h2, .mongodb-post-area .rt-h3');
                        if (firstHeading) {
                            tocContainerBottom = (firstHeading.offsetTop || 0) - 50;
                        }
                    }
                } else {
                    // Fallback: use first heading if TOC container not found
                    const firstHeading = document.querySelector('.rt-h1, .rt-h2, .rt-h3, .mongodb-post-area .rt-h1, .mongodb-post-area .rt-h2, .mongodb-post-area .rt-h3');
                    if (firstHeading) {
                        tocContainerBottom = (firstHeading.offsetTop || 0) - 50;
                    } else {
                        setIsVisible(false);
                        return;
                    }
                }
                
                // Show/hide dynamic TOC based on scroll position
                const shouldShowToc = scrollY > tocContainerBottom && scrollY < blogContentEnd - 300;
                
                if (shouldShowToc) {
                    // Always update heading when TOC is visible (regardless of expanded state)
                    // This ensures heading updates even when TOC is not expanded
                    let currentVisibleHeading = '';
                    const stickyHeaderOffset = 100; // Offset for sticky header + TOC
                    
                    try {
                        // Find the last heading that has passed the sticky header area
                        // This is the most reliable method - find the heading closest to or just above the sticky header
                        for (let i = headings.length - 1; i >= 0; i--) {
                            const heading = headings[i];
                            if (!heading || !heading.id) continue;
                            
                            let element = document.getElementById(heading.id);
                            
                            // If not found by ID, try to find by text content and class
                            if (!element) {
                                const allHeadings = document.querySelectorAll('.rt-h1, .rt-h2, .rt-h3, .rt-h4, .rt-h5, .rt-h6, .mongodb-post-area .rt-h1, .mongodb-post-area .rt-h2, .mongodb-post-area .rt-h3, .mongodb-post-area .rt-h4, .mongodb-post-area .rt-h5, .mongodb-post-area .rt-h6');
                                allHeadings.forEach(h => {
                                    const text = (h.textContent || h.innerText || '').trim();
                                    if (text === heading.text) {
                                        element = h;
                                    }
                                });
                            }
                            
                            if (element) {
                                try {
                                    const rect = element.getBoundingClientRect();
                                    const headingTop = rect.top;
                                    
                                    // If heading has passed the sticky header area (top is above the offset)
                                    // This is the current section we're reading
                                    if (headingTop <= stickyHeaderOffset + 50) {
                                        currentVisibleHeading = heading.text || '';
                                        break; // Found the current heading, stop searching
                                    }
                                } catch (e) {
                                    // Ignore errors
                                }
                            }
                        }
                        
                        // If no heading found (we're at the top), use first heading
                        if (!currentVisibleHeading && headings.length > 0) {
                            currentVisibleHeading = headings[0].text || '';
                        }
                    } catch (e) {
                        // Ignore errors, use first heading as fallback
                        if (headings.length > 0) {
                            currentVisibleHeading = headings[0].text || '';
                        }
                    }
                    
                    // Update heading state - CRITICAL: Always update when TOC is visible
                    // This ensures heading updates even when TOC is not expanded
                    const newHeading = currentVisibleHeading || (headings[0] && headings[0].text) || 'Table of Contents';
                    setCurrentHeading(prev => {
                        // Always update to ensure heading changes on scroll, even if value appears same
                        // This forces React to re-render the heading text
                        if (prev !== newHeading) {
                            return newHeading;
                        }
                        // If same, still return it to ensure state is fresh
                        return newHeading;
                    });
                    
                    // Only update visibility state if it changed
                    setIsVisible(prev => {
                        if (!prev) {
                            return true;
                        }
                        return prev;
                    });
                } else {
                    // Hide TOC when scrolled out of content area
                    setIsVisible(prev => {
                        if (prev) {
                            return false;
                        }
                        return prev;
                    });
                }
            } catch (error) {
                setIsVisible(false);
            }
        };

        // Throttle scroll handler more aggressively to prevent re-renders
        let ticking = false;
        let lastScrollY = 0;
        const throttledHandleScroll = () => {
            const currentScrollY = window.scrollY || window.pageYOffset || 0;
            // Only process if scroll changed significantly (more than 10px)
            if (Math.abs(currentScrollY - lastScrollY) < 10 && ticking) {
                return;
            }
            lastScrollY = currentScrollY;
            
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
        // Initial check after a delay to let DOM settle
        setTimeout(() => handleScroll(), 100);

        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
            cacheValid = false;
            cachedTocContainer = null;
            cachedBlogContent = null;
        };
    }, [headings]);

    const handleHeadingClick = (headingId) => {
        // Try to find the element by ID
        let element = document.getElementById(headingId);
        
        // If not found, try to find by class and text match
        if (!element) {
            const headings = document.querySelectorAll('.rt-h1, .rt-h2, .rt-h3, .mongodb-post-area .rt-h1, .mongodb-post-area .rt-h2, .mongodb-post-area .rt-h3');
            headings.forEach(h => {
                const text = h.textContent || h.innerText || '';
                const id = text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '');
                if (id === headingId) {
                    element = h;
                }
            });
        }
        
        if (element) {
            const offset = 100; // Offset for sticky header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setIsExpanded(false);
        }
    };

    if (!isVisible || !headings || headings.length === 0) {
        return null;
    }

    return (
        <div className={`dynamic-toc-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            {!isExpanded && (
                <div className="dynamic-toc-header" onClick={() => setIsExpanded(!isExpanded)}>
                    <span className="dynamic-toc-current-heading">
                        {currentHeading || 'Table of Contents'}
                    </span>
                    <i className={`fi fi-rr-caret-down dynamic-toc-arrow ${isExpanded ? 'expanded' : ''}`}></i>
                </div>
            )}
            
            {isExpanded && (
                <div className="dynamic-toc-dropdown">
                    <div className="dynamic-toc-dropdown-header" onClick={() => setIsExpanded(false)}>
                        <span className="dynamic-toc-current-heading">
                            {currentHeading || 'Table of Contents'}
                        </span>
                        <i className="fi fi-rr-caret-up dynamic-toc-arrow"></i>
                    </div>
                    <ul className="dynamic-toc-list">
                        {headings.map((heading, index) => (
                            <li key={index} className="dynamic-toc-item">
                                <button 
                                    className={`dynamic-toc-link ${currentHeading === heading.text ? 'active' : ''}`}
                                    onClick={() => handleHeadingClick(heading.id)}
                                >
                                    {heading.text}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ========== DYNAMIC TABLE OF CONTENTS FEATURE END ==========

// Global asset map to store all assets from the main query
const globalAssetMap = new Map();

// Removed global assets cache - using fallback approach instead

// Cache for individual asset fetches to avoid repeated API calls
const assetFetchCache = new Map(); // eslint-disable-line no-unused-vars

// Rate limiting for API calls
let lastApiCall = 0; // eslint-disable-line no-unused-vars
const API_CALL_DELAY = 500; // eslint-disable-line no-unused-vars

// eslint-disable-next-line no-unused-vars
function populateGlobalAssetMap(assets) {
    if (!assets || !Array.isArray(assets)) {
        return;
    }
    
    
    // Clear existing map
    globalAssetMap.clear();
    
    // Populate the map with comprehensive asset data
    assets.forEach(asset => {
        if (asset.url) {
            // Priority order: imageTitle > altText > fileName (cleaned)
            const title = asset.imageTitle || 
                         asset.altText || 
                         (asset.fileName ? asset.fileName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Image');
            
            globalAssetMap.set(asset.url, {
                title: title,
                fileName: asset.fileName,
                altText: asset.altText,
                description: null, // Not available in simplified structure
                width: null, // Not available in simplified structure
                height: null, // Not available in simplified structure
                mimeType: null // Not available in simplified structure
            });
            
        }
    });
    
}

// Removed global assets cache function - using fallback approach instead

// Function to get fallback image title from filename
function getFallbackImageTitle(imageUrl) {
    
        const imageUrlParts = imageUrl.split('/');
        const filename = imageUrlParts[imageUrlParts.length - 1];
        const baseName = filename.split('.')[0];
        
    let fallbackTitle = 'Product Image';
    
    // Try to make a more meaningful title from the filename
    if (baseName && baseName.length > 3 && !baseName.match(/^[a-z0-9]{20,}$/i)) {
        // If filename is not just a long hash, convert to title
            fallbackTitle = baseName
                .replace(/[_-]/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
        }
        
        return fallbackTitle;
}

// Removed individual asset fetching functions to avoid 400/429 errors
// Now using cached approach similar to blog posts

// Function to format bio with clickable links
function formatBioWithLinks(bio) {
    if (!bio) return "No bio available";
    
    // Regular expression to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Split bio by URLs and create clickable links
    const parts = bio.split(urlRegex);
    
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <a 
                    key={index} 
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    {part}
                </a>
            );
        }
        return part;
    });
}

// Function to identify social media platform from URL
function getSocialMediaPlatform(url) {
    if (!url || typeof url !== 'string') return null;
    
    try {
        // Add http:// if protocol is missing
        let urlToParse = url.trim();
        if (!urlToParse.match(/^https?:\/\//i)) {
            urlToParse = 'https://' + urlToParse;
        }
        
        const urlObj = new URL(urlToParse);
        const hostname = urlObj.hostname.toLowerCase();
        
        // Remove www. prefix
        const domain = hostname.replace(/^www\./, '');
        
        // Map domains to platform names
        const platformMap = {
            'github.com': 'github',
            'linkedin.com': 'linkedin',
            'instagram.com': 'instagram',
            'youtube.com': 'youtube',
            'twitter.com': 'twitter',
            'x.com': 'twitter',
            'threads.net': 'threads',
            'threads.com': 'threads',
            'facebook.com': 'facebook',
            'fb.com': 'facebook',
            'tiktok.com': 'tiktok',
            'pinterest.com': 'pinterest',
            'snapchat.com': 'snapchat',
            'reddit.com': 'reddit',
            'medium.com': 'medium',
            'behance.net': 'behance',
            'dribbble.com': 'dribbble',
            'discord.com': 'discord',
            'telegram.org': 'telegram',
            'whatsapp.com': 'whatsapp'
        };
        
        return platformMap[domain] || null;
    } catch (e) {
        return null;
    }
}

// Function to get icon class for social media platform
function getSocialIconClass(platform) {
    const iconMap = {
        'github': 'fi fi-brands-github',
        'linkedin': 'fi fi-brands-linkedin',
        'instagram': 'fi fi-brands-instagram',
        'youtube': 'fi fi-brands-youtube',
        'twitter': 'fi fi-brands-twitter-alt',
        'threads': 'fi fi-brands-threads',
        'facebook': 'fi fi-brands-facebook',
        'tiktok': 'fi fi-brands-tiktok',
        'pinterest': 'fi fi-brands-pinterest',
        'snapchat': 'fi fi-brands-snapchat',
        'reddit': 'fi fi-brands-reddit',
        'medium': 'fi fi-brands-medium',
        'behance': 'fi fi-brands-behance',
        'dribbble': 'fi fi-brands-dribbble',
        'discord': 'fi fi-brands-discord',
        'telegram': 'fi fi-brands-telegram',
        'whatsapp': 'fi fi-brands-whatsapp'
    };
    
    return iconMap[platform] || 'fi fi-brands-link';
}

// Author Popup Component
function AuthorPopup({ authors, initialAuthorIndex = 0, isOpen, onClose }) {
    const [currentIndex, setCurrentIndex] = useState(initialAuthorIndex);

    // Reset index when popup opens or authors change
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialAuthorIndex);
        }
    }, [isOpen, initialAuthorIndex]);

    const author = authors && authors[currentIndex];

    // Prevent background scrolling when popup is open
    useEffect(() => {
        if (isOpen) {
            // Save current scroll position
            const scrollY = window.scrollY;
            // Disable body scroll
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            
            return () => {
                // Restore scroll position when popup closes
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    const handleNext = (e) => {
        e.stopPropagation();
        if (authors && currentIndex < authors.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
             // Loop back to start
             setCurrentIndex(0);
        }
    };

    const handlePrev = (e) => {
        e.stopPropagation();
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        } else {
            // Loop to end
            setCurrentIndex(authors.length - 1);
        }
    };

    if (!isOpen) return null;
    if (!author) return null;


    // Process social links - handle both object format (MongoDB) and string format (HyGraph)
    const socialLinks = (author.socialLinks || [])
        .map(link => {
            // Handle object format: { platform: 'github', url: 'https://...' }
            if (link && typeof link === 'object' && link.url) {
                let url = link.url.trim();
                // Ensure URL has protocol
                if (url && !url.match(/^https?:\/\//i)) {
                    url = 'https://' + url;
                }
                // Use provided platform or detect from URL
                const platform = (link.platform && link.platform.toLowerCase()) || getSocialMediaPlatform(url);
                if (platform && url) {
                    return {
                        url: url,
                        platform: platform,
                        iconClass: getSocialIconClass(platform)
                    };
                }
            }
            // Handle string format: 'https://github.com/...'
            else if (link && typeof link === 'string' && link.trim()) {
                let trimmedLink = link.trim();
                // Ensure URL has protocol
                if (!trimmedLink.match(/^https?:\/\//i)) {
                    trimmedLink = 'https://' + trimmedLink;
                }
                const platform = getSocialMediaPlatform(trimmedLink);
                if (platform && trimmedLink) {
                    return {
                        url: trimmedLink,
                        platform: platform,
                        iconClass: getSocialIconClass(platform)
                    };
                }
            }
            return null;
        })
        .filter(item => item !== null && item.platform !== null && item.url); // Only show recognized platforms with valid URLs

    return (
        <div className="author-popup-overlay" onClick={onClose}>
            <div className="author-popup" onClick={(e) => e.stopPropagation()}>
                <div className="author-popup-header">
                    <span className="author-popup-title">Profile</span>
                    <div className="author-popup-closebtn" onClick={onClose}>
                        <p>Esc</p>
                        <i id="close_btn" className="fi fi-rr-cross-small"></i>
                    </div>
                </div>
                <div className="author-popup-divider"></div>
                <div className="author-popup-content-wrapper">
                    {/* Left side - Image, Name, Bio */}
                    <div className="author-popup-left">
                        <img 
                            className="author-popup-image" 
                            src={author.image} 
                            alt={`${author.name} profile`}
                            onError={(e) => {
                                e.target.src = "/images/home_images/Logo.png";
                            }}
                        />
                        <div className="author-popup-name">{author.name}</div>
                        <div className="author-popup-bio">
                            {formatBioWithLinks(author.bio)}
                        </div>
                    </div>
                    
                    {/* Right side - Social Media Icons */}
                    <div className="author-popup-right">
                        {socialLinks.length > 0 ? (
                            <div className="author-popup-social-icons">
                                {socialLinks.map((social, index) => (
                                    <a
                                        key={index}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`author-popup-social-icon ${social.platform}`}
                                        title={social.platform.charAt(0).toUpperCase() + social.platform.slice(1)}
                                    >
                                        <i className={social.iconClass}></i>
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <div className="author-popup-no-social" style={{ 
                                fontSize: '12px', 
                                color: '#999', 
                                textAlign: 'center', 
                                padding: '10px'
                            }}>
                                No social links
                            </div>
                        )}
                    </div>
                </div>
                {/* Navigation Arrows - Only show if multiple authors */}
                {authors.length > 1 && (
                    <div className="author-popup-navigation">
                         <button className="author-nav-btn prev" onClick={handlePrev} title="Previous Author">
                            <i className="fi fi-rr-angle-left"></i>
                        </button>
                        <div className="author-nav-dots">
                            {authors.map((_, idx) => (
                                <span 
                                    key={idx} 
                                    className={`author-nav-dot ${idx === currentIndex ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                                />
                            ))}
                        </div>
                        <button className="author-nav-btn next" onClick={handleNext} title="Next Author">
                            <i className="fi fi-rr-angle-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}


// Removed assets query to avoid 400 errors - using fallback approach instead

function formatPublishedDateTime(isoString) {
    try {
        if (!isoString) return "";
        const published = new Date(isoString);
        if (isNaN(published.getTime())) return "";

        const dateStr = published.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
        const timeStr = published.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
        
        return `${dateStr}, ${timeStr} IST`;
    } catch (_) {
        return "";
    }
}

function formatRelativeOrDate(isoString){
    try{
        if (!isoString) return "Recently";
        const published = new Date(isoString);
        // Check if date is valid
        if (isNaN(published.getTime())) {
            return "Recently";
        }
        const now = new Date();
        const diffMs = now - published;
        const diffHours = Math.floor(diffMs / (1000*60*60));
        const diffMinutes = Math.floor(diffMs / (1000*60));
        
        // Show relative time if within last 24 hours
        if (diffHours < 24){
            if (diffMinutes < 1) return "just now";
            if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes===1?"":"s"} ago`;
            return `${diffHours} hour${diffHours===1?"":"s"} ago`;
        }
        
        // Show actual date if more than 24 hours
        return published.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }catch(_){
        return "Recently";
    }
}

const getOrdinalSuffix = (number) => {
    if (!Number.isInteger(number)) return "";
    
    const remainder = number % 100;
    
    if (remainder >= 11 && remainder <= 13) return `${number}th`; // Special case for 11, 12, 13
    
    switch (number % 10) {
        case 1:
            return `st`;
        case 2:
            return `nd`;
        case 3:
            return `rd`;
        default:
            return `th`;
    }
};

// Robustly get text from any React children tree and derive a slug ID
const getPlainText = (node) => {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getPlainText).join("");
  if (React.isValidElement(node)) return getPlainText(node.props && node.props.children);
      return "";
};

function stableHash(input){
  const str = String(input || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// eslint-disable-next-line no-unused-vars
const makeIdFromChildren = (children) => {
  const raw = getPlainText(children);
  const slug = raw
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || `h-${stableHash(raw)}`;
};

function InlineBundleStyles(){
    return (
        <style>{`
            .ibundle{ width: 90%; max-width: 1000px; margin: 2% auto; }
            .ibundle-frame{ position: relative; width: 100%; padding-top: 56.25%; border: 1px solid var(--slider-color); border-radius: 12px; overflow: hidden; background: var(--bg-color); }
            .ibundle-image{ position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; background: var(--bg-color); }
            .ibundle-controls{ display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-top: 8px; }
            .ibundle-desc{ color: var(--post-heading-font); font-style: italic; flex: 1; word-wrap: break-word; hyphens: auto; }
            .ibundle-nav{ display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
            .ibtn{ height: 24px; width: 24px; border: 0.5px solid #ccc; background: var(--bg-color); border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
            .ibtn:disabled{ opacity: 0.5; cursor: not-allowed; }
            .ibundle-counter{ margin: 0 8px; color: var(--post-heading-font); font-weight: 600; white-space: nowrap; }
            .ibundle-fullscreen{ position: fixed; inset: 0; background: rgba(0,0,0,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999; }
            .ibundle-fullscreen .ibundle-frame{ width: 90vw; padding-top: 56.25%; border: none; }
            .ibundle-fullscreen .ibundle-controls{ width: 90vw; }
            
            /* Mobile responsive fixes */
            @media (max-width: 768px) {
                .ibundle-controls{ flex-direction: row; align-items: center; gap: 8px; }
                .ibundle-desc{ flex: 1; text-align: left; margin: 0; }
                .ibundle-nav{ flex-shrink: 0; }
                .ibtn{ height: 20px; width: 20px; }
                .ibundle-counter{ font-size: 0.9em; }
            }
        `}</style>
    );
}

function InlineBundleCarousel({ items }){
    const [index, setIndex] = useState(0);
    const [isFull, setIsFull] = useState(false);
    const total = items.length;
    const goPrev = () => setIndex((i)=> (i===0? total-1 : i-1));
    const goNext = () => setIndex((i)=> (i===total-1? 0 : i+1));
    const current = items[index];
    
    // Debug logging
    useEffect(() => {
        // eslint-disable-next-line no-unused-vars
        const currentUrl = current && current.url ? current.url : null;
    }, [items, current]);
    useEffect(() => {
        if (isFull) {
            const prevBodyOverflow = document.body.style.overflow;
            const prevHtmlOverflow = document.documentElement.style.overflow;
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            const preventTouch = (e) => e.preventDefault();
            document.addEventListener('touchmove', preventTouch, { passive: false });
            const onKey = (e) => { if (e.key === 'Escape') setIsFull(false); };
            document.addEventListener('keydown', onKey);
            return () => {
                document.body.style.overflow = prevBodyOverflow;
                document.documentElement.style.overflow = prevHtmlOverflow;
                document.removeEventListener('touchmove', preventTouch);
                document.removeEventListener('keydown', onKey);
            };
        }
    }, [isFull]);
    return (
        <div
            className={`ibundle${isFull? ' ibundle-fullscreen' : ''}`}
            onWheel={(e)=> { if (isFull) e.preventDefault(); }}
            onClick={isFull ? () => setIsFull(false) : undefined}
        >
            <div className="ibundle-frame">
                <img className="ibundle-image" src={current.url} alt={current.description || ''} />
            </div>
            <div className={`ibundle-controls${isFull? ' ibundle-controls-bottom' : ''}`} style={{pointerEvents: 'auto'}} onClick={(e)=> e.stopPropagation()}>
                <div className="ibundle-desc">{current.description}</div>
                <div className="ibundle-nav">
                    <button className="ibtn" onClick={goPrev} aria-label="Previous"><i className="fi fi-rr-caret-left"></i></button>
                    <span className="ibundle-counter">{index+1} / {total}</span>
                    <button className="ibtn" onClick={goNext} aria-label="Next"><i className="fi fi-rr-caret-right"></i></button>
                </div>
            </div>
        </div>
    );
}

function InlineBundleSingle({ url, description }){
    // Debug logging
    if (!url) {
        return null;
    }
    
    return (
        <div className="ibundle">
            <div className="ibundle-frame">
                <img 
                    className="ibundle-image" 
                    src={url} 
                    alt={description || ''} 
                    onError={(e) => {
                        // Silently handle image load errors
                    }}
                />
            </div>
            <div className="ibundle-controls">
                <div className="ibundle-desc">{description}</div>
            </div>
        </div>
    );
}

function GroupedRichText({ content, references, imageParagraphs = [], imageGroups = [] }){
    
    const containerRef = useRef(null);
    const processedRef = useRef(false);
    const imageParagraphsRef = useRef(imageParagraphs);
    const imageGroupsRef = useRef(imageGroups);
    const imagesProcessedPermanentlyRef = useRef(false); // Permanent flag that never resets
    const tableIndexRef = useRef(0); // Track which table we're rendering
    
    // Update refs when props change
    useEffect(() => {
        imageParagraphsRef.current = imageParagraphs;
        imageGroupsRef.current = imageGroups;
    }, [imageParagraphs, imageGroups]);
    
    // Create a map for quick lookup of image data by index
    const imageDataMap = useMemo(() => {
        const map = new Map();
        imageParagraphs.forEach(({ index, imageData }) => {
            map.set(index, imageData);
        });
        return map;
    }, [imageParagraphs]);
    
    // Note: Moved cache population to main Post component
    
    // Create a map of image URLs to their titles from references and global cache
    const imageTitleMap = useMemo(() => {
        const map = new Map();
        
        // First, populate from global asset map (most comprehensive)
        globalAssetMap.forEach((assetData, url) => {
            map.set(url, assetData.title);
        });
        
        // Then, get from references (for any additional data)
        if (references && Array.isArray(references)) {
            references.forEach(ref => {
                // Check for ImageBundle
                if (ref.__typename === 'ImageBundle' && ref.images) {
                    ref.images.forEach(img => {
                        if (img.url && img.imageTitle) {
                            map.set(img.url, img.imageTitle);
                        }
                    });
                }
                // Check for direct Asset references
                else if (ref.__typename === 'Asset' && ref.url && ref.imageTitle) {
                    map.set(ref.url, ref.imageTitle);
                }
            });
        }
        
        
        return map;
    }, [references]);
    
    useEffect(() => {
        // Add error boundary to prevent blank page
        const processImages = async () => {
            try {
                const container = containerRef.current;
                if (!container) {
                    return;
                }

                // PERMANENT CHECK: If images have been processed once, NEVER process again
                if (imagesProcessedPermanentlyRef.current) {
                    return;
                }

                // Check if already processed in this component instance
                if (processedRef.current) {
                    return;
                }

                // Check if container has been processed before
                if (container.dataset.processed === 'true') {
                    imagesProcessedPermanentlyRef.current = true;
                    return;
                }
                
                // Check if images are already rendered (prevent re-processing on re-renders)
                const existingImages = container.querySelectorAll('.ibundle-processed, .ibundle');
                if (existingImages.length > 0 && imageParagraphsRef.current && imageParagraphsRef.current.length > 0) {
                    // Images already exist, mark as processed PERMANENTLY and skip
                    processedRef.current = true;
                    imagesProcessedPermanentlyRef.current = true;
                    container.dataset.processed = 'true';
                    return;
                }
                
                // Wait for content to be ready - check if we have image paragraphs
                if (!imageParagraphsRef.current || imageParagraphsRef.current.length === 0) {
                    // Not ready yet, don't mark as processed, just return
                    // The useEffect will run again when imageParagraphs/imageGroups change
                    return;
                }
                
                // Mark as processing to prevent concurrent runs
                // But only if we're actually going to process
                processedRef.current = true;
                container.dataset.processed = 'processing';
                
                // FALLBACK: Process image placeholders and group consecutive ones into carousels
                if (imageParagraphsRef.current && imageParagraphsRef.current.length > 0 && imageGroupsRef.current) {
                    // Wait a bit for RichText to render
                    setTimeout(() => {
                        try {
                            // Double-check we haven't been processed by another instance
                            if (container.dataset.processed === 'true' || imagesProcessedPermanentlyRef.current) {
                                return;
                            }
                            
                            // Don't reset processed flag - keep it to prevent re-processing
                            // processedRef.current stays true to prevent concurrent runs
                            // imagesProcessedPermanentlyRef.current will be set to true after processing
                            
                    const paragraphs = Array.from(container.querySelectorAll('p.rt-para'));
                    const processedIndices = new Set();
                    
                    // Helper function to get DOM position of an element
                    const getDOMPosition = (element) => {
                        if (!element) return 999999;
                        // Get all elements in the container (including nested ones)
                        const allElements = container.querySelectorAll('*');
                        const elementArray = Array.from(allElements);
                        return elementArray.indexOf(element);
                    };
                    
                    // Helper function to find the correct parent for insertion
                    const findInsertionParent = (para) => {
                        if (!para) return container;
                        // Walk up to find a direct child of container or a paragraph's parent
                        let current = para;
                        while (current && current.parentElement) {
                            const parent = current.parentElement;
                            // If parent is the container or a direct child structure, use it
                            if (parent === container || parent.classList.contains('rich-text-comp')) {
                                return parent;
                            }
                            // If parent is a div that's a direct child of container, use it
                            if (parent.parentElement === container) {
                                return parent;
                            }
                            current = parent;
                        }
                        return container;
                    };
                    
                    // Create a map of all image indices to their DOM positions
                    // This ensures we process images in the order they appear in the content
                    const imagePositionMap = new Map();
                    paragraphs.forEach((para) => {
                        const text = para.textContent || para.innerText || '';
                        const match = text.match(/__IMAGE_PLACEHOLDER_(\d+)__/) || text.match(/IMAGE_PLACEHOLDER[_\s]*(\d+)/);
                        if (match) {
                            const index = parseInt(match[1], 10);
                            const position = getDOMPosition(para);
                            imagePositionMap.set(index, { para, position, insertionParent: findInsertionParent(para) });
                        }
                    });
                    
                    // Sort image groups by their first image's position in DOM (content order)
                    const sortedGroups = [...imageGroupsRef.current].sort((groupA, groupB) => {
                        const firstIndexA = groupA[0] && groupA[0].index;
                        const firstIndexB = groupB[0] && groupB[0].index;
                        if (!firstIndexA || !firstIndexB) return 0;
                        
                        const posDataA = imagePositionMap.get(firstIndexA);
                        const posDataB = imagePositionMap.get(firstIndexB);
                        const posA = posDataA && posDataA.position ? posDataA.position : 999999;
                        const posB = posDataB && posDataB.position ? posDataB.position : 999999;
                        return posA - posB;
                    });
                    
                    // Process image groups (consecutive images should become carousels)
                    // IMPORTANT: Process in order of appearance in content to maintain position
                    sortedGroups.forEach((group, groupIndex) => {
                        if (group.length === 0) return;
                        
                        // Find all placeholder paragraphs for this group
                        const groupParagraphs = [];
                        group.forEach(({ index }) => {
                            paragraphs.forEach((para) => {
                                if (processedIndices.has(index)) return;
                                const text = para.textContent || para.innerText || '';
                                const match = text.match(/__IMAGE_PLACEHOLDER_(\d+)__/) || text.match(/IMAGE_PLACEHOLDER[_\s]*(\d+)/);
                                if (match && parseInt(match[1], 10) === index) {
                                    groupParagraphs.push({ para, index });
                                    processedIndices.add(index);
                                }
                            });
                        });
                        
                        if (groupParagraphs.length === 0) return;
                        
                        // Sort by DOM order (position in the rendered content)
                        groupParagraphs.sort((a, b) => {
                            // Get all children of container
                            const containerChildren = Array.from(container.children);
                            // Find the paragraph's position
                            const getPosition = (para) => {
                                let element = para;
                                // Walk up to find the direct child of container
                                while (element && element.parentElement !== container) {
                                    element = element.parentElement;
                                }
                                return containerChildren.indexOf(element);
                            };
                            return getPosition(a.para) - getPosition(b.para);
                        });
                        
                        // Create items array for carousel or single image
                        const items = group.map(({ imageData }) => {
                            const url = imageData.src;
                            const description = imageData.title || imageData.alt || 'Image';
                            return { url, description };
                        });
                        
                        // Verify items have valid URLs
                        const validItems = items.filter(item => item.url && item.url.trim());
                        if (validItems.length === 0) {
                            return;
                        }
                        
                        // Get the first paragraph and its insertion parent
                        const firstParaData = imagePositionMap.get(group[0].index);
                        if (!firstParaData || !firstParaData.para) return;
                        
                        const firstPara = firstParaData.para;
                        const insertionParent = firstParaData.insertionParent || firstPara.parentNode || container;
                        
                        // Hide all placeholder paragraphs FIRST before inserting
                        // This prevents React DOM conflicts
                        groupParagraphs.forEach(({ para }) => {
                            try {
                                if (para) {
                                    para.style.display = 'none';
                                    para.classList.add('ibundle-placeholder-hidden');
                                    // Also hide the text content
                                    if (para.textContent) {
                                        para.textContent = '';
                                    }
                                }
                            } catch (error) {
                                // Silent error handling
                            }
                        });
                        
                        // Create mount point and insert at the EXACT position of the first placeholder
                        const mount = document.createElement('div');
                        mount.className = 'ibundle-processed';
                        
                        // Insert at the correct position - right where the placeholder paragraph is
                        // Use the insertion parent we found
                        if (firstPara && insertionParent) {
                            // Insert right before the hidden paragraph in the correct parent
                            if (firstPara.parentNode === insertionParent) {
                                insertionParent.insertBefore(mount, firstPara);
                            } else {
                                // If paragraph is nested deeper, find its position in the insertion parent
                                const siblings = Array.from(insertionParent.children);
                                let insertIndex = -1;
                                for (let i = 0; i < siblings.length; i++) {
                                    if (siblings[i].contains(firstPara) || siblings[i] === firstPara) {
                                        insertIndex = i;
                                        break;
                                    }
                                }
                                if (insertIndex >= 0 && insertIndex < siblings.length) {
                                    insertionParent.insertBefore(mount, siblings[insertIndex]);
                                } else {
                                    insertionParent.insertBefore(mount, firstPara);
                                }
                            }
                        } else {
                            // Fallback: append to container
                            container.appendChild(mount);
                        }
                        
                        // Render carousel or single image
                        try {
                            const root = createRoot(mount);
                            if (validItems.length > 1) {
                                // Multiple images - use carousel
                                root.render(React.createElement(InlineBundleCarousel, { items: validItems }));
                                
                                // Verify rendering after a short delay
                                setTimeout(() => {
                                    const img = mount.querySelector('.ibundle-image');
                                    if (img) {
                                    }
                                }, 200);
                            } else if (validItems.length === 1) {
                                // Single image - use single component
                                root.render(React.createElement(InlineBundleSingle, { 
                                    url: validItems[0].url, 
                                    description: validItems[0].description 
                                }));
                                
                                // Verify rendering after a short delay
                                setTimeout(() => {
                                    const img = mount.querySelector('.ibundle-image');
                                    if (img) {
                                    }
                                }, 200);
                            }
                        } catch(error) {
                            // Silent error handling
                        }
                    });
                    
                    // Process any remaining single images that weren't in groups
                    // IMPORTANT: Process in DOM order to maintain position
                    const remainingParagraphs = paragraphs.filter(para => {
                        const text = para.textContent || para.innerText || '';
                        const match = text.match(/__IMAGE_PLACEHOLDER_(\d+)__/) || text.match(/IMAGE_PLACEHOLDER[_\s]*(\d+)/);
                        if (!match) return false;
                        const placeholderIndex = parseInt(match[1], 10);
                        return !processedIndices.has(placeholderIndex);
                    });
                    
                    // Sort by DOM position to maintain order
                    remainingParagraphs.sort((a, b) => {
                        const containerChildren = Array.from(container.children);
                        const getPosition = (para) => {
                            let element = para;
                            while (element && element.parentElement !== container) {
                                element = element.parentElement;
                            }
                            return containerChildren.indexOf(element);
                        };
                        return getPosition(a) - getPosition(b);
                    });
                    
                    remainingParagraphs.forEach((para) => {
                        if (processedIndices.size >= imageParagraphsRef.current.length) return;
                        
                        const text = para.textContent || para.innerText || '';
                        const match = text.match(/__IMAGE_PLACEHOLDER_(\d+)__/) || text.match(/IMAGE_PLACEHOLDER[_\s]*(\d+)/);
                        
                        if (match) {
                            const placeholderIndex = parseInt(match[1], 10);
                            if (processedIndices.has(placeholderIndex)) return;
                            
                            const imagePara = imageParagraphsRef.current.find(ip => ip.index === placeholderIndex);
                            
                            if (imagePara && imagePara.imageData && imagePara.imageData.src) {
                                const imageUrl = imagePara.imageData.src;
                                const imageDesc = imagePara.imageData.title || imagePara.imageData.alt || 'Image';
                                
                                if (!imageUrl || !imageUrl.trim()) {
                                    return;
                                }
                                
                                processedIndices.add(placeholderIndex);
                                
                                // Hide the placeholder paragraph FIRST
                                try {
                                    if (para) {
                                        para.style.display = 'none';
                                        para.classList.add('ibundle-placeholder-hidden');
                                        // Clear the text content to hide placeholder text
                                        if (para.textContent) {
                                            para.textContent = '';
                                        }
                                    }
                                } catch (error) {
                                    // Silent error handling
                                }
                                
                                // Find the correct insertion parent
                                const findInsertionParent = (para) => {
                                    if (!para) return container;
                                    let current = para;
                                    while (current && current.parentElement) {
                                        const parent = current.parentElement;
                                        if (parent === container || parent.classList.contains('rich-text-comp')) {
                                            return parent;
                                        }
                                        if (parent.parentElement === container) {
                                            return parent;
                                        }
                                        current = parent;
                                    }
                                    return para.parentNode || container;
                                };
                                
                                const insertionParent = findInsertionParent(para);
                                
                                // Create single image component
                                const mount = document.createElement('div');
                                mount.className = 'ibundle-processed';
                                
                                // Insert at the EXACT position of the placeholder paragraph
                                if (para && insertionParent) {
                                    if (para.parentNode === insertionParent) {
                                        insertionParent.insertBefore(mount, para);
                                    } else {
                                        // Find the right position in insertion parent
                                        const siblings = Array.from(insertionParent.children);
                                        let insertIndex = -1;
                                        for (let i = 0; i < siblings.length; i++) {
                                            if (siblings[i].contains(para) || siblings[i] === para) {
                                                insertIndex = i;
                                                break;
                                            }
                                        }
                                        if (insertIndex >= 0 && insertIndex < siblings.length) {
                                            insertionParent.insertBefore(mount, siblings[insertIndex]);
                                        } else {
                                            insertionParent.insertBefore(mount, para);
                                        }
                                    }
                                } else {
                                    // Fallback: append to container
                                    container.appendChild(mount);
                                }
                                
                                try {
                                    const root = createRoot(mount);
                                    root.render(React.createElement(InlineBundleSingle, {
                                        url: imageUrl,
                                        description: imageDesc
                                    }));
                                    
                                    // Verify rendering after a short delay
                                    setTimeout(() => {
                                        const img = mount.querySelector('.ibundle-image');
                                        if (img) {
                                        }
                                    }, 200);
                                } catch(error) {
                                    // Silent error handling
                                }
                            }
                        }
                    });
                    
                            if (processedIndices.size > 0) {
                            }
                        } catch (error) {
                            // Silent error handling
                        }
                    }, 150);
                }
                
                // Mark as fully processed PERMANENTLY only after all processing is done
                // Use a longer timeout to ensure all images are rendered
                setTimeout(() => {
                    processedRef.current = true;
                    imagesProcessedPermanentlyRef.current = true; // PERMANENT FLAG
                    if (container) {
                        container.dataset.processed = 'true';
                    }
                }, 500);
            } catch (error) {
                // Silent error handling
                // Don't throw - just log to prevent blank page
            }
        };
        
        // Process images when content is ready
        // BUT ONLY IF NOT ALREADY PROCESSED PERMANENTLY
        if (!imagesProcessedPermanentlyRef.current) {
            // Use a small delay to ensure DOM is ready
            const timeoutId = setTimeout(() => {
                processImages();
            }, 50);
            
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [imageParagraphs, imageGroups]); // Only re-run if image data actually changes, not on every render
    
    // Helper functions for image grouping (moved outside useEffect)
    const container = containerRef.current;
    const isRtImg = (el) => el && el.classList && el.classList.contains('rt') && el.classList.contains('rt-img');
    const slugify = (text) => String(text || '').toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '');
    const stableHash = (input) => {
        const str = String(input || "");
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0;
        }
        return Math.abs(hash).toString(36);
    };
    
    const ensureHeadingIds = () => {
        if (!container) return;
        const headingSelector = '.rt.rt-h1, .rt.rt-h2, .rt.rt-h3, .rt.rt-h4, .rt.rt-h5, .rt.rt-h6';
        const els = Array.from(container.querySelectorAll(headingSelector));
        const used = new Set();
        els.forEach((el) => {
            const text = (el.innerText || el.textContent || '').trim();
            const base = slugify(text) || `h-${stableHash(text)}`;
            let id = base;
            let n = 2;
            while (used.has(id)) { id = `${base}-${n++}`; }
            used.add(id);
            if (el.id !== id) el.id = id;
        });
    };
    
    const isEmptyPara = (el) => {
        if (!el || el.tagName !== 'P') return false;
        if (!el.classList.contains('rt-para')) return false;
        const text = (el.textContent || '').trim();
        return text === '' && el.children.length === 0;
    };

    // Second useEffect for image grouping (separate from fallback processing)
    useEffect(() => {
        if (!containerRef.current) return;
        const container = containerRef.current;
        
        // eslint-disable-next-line no-unused-vars
        const groupNow = () => {
            let ptr = container.firstElementChild;
            while (ptr){
                if (isRtImg(ptr) && !ptr.classList.contains('ibundle-processed') && !ptr.classList.contains('ibundle')){
                    const groupNodes = [];
                    let cursor = ptr;
                    while (cursor && (isRtImg(cursor) || isEmptyPara(cursor))){
                        if (isRtImg(cursor)) groupNodes.push(cursor);
                        cursor = cursor.nextElementSibling;
                    }
                    if (groupNodes.length > 1){
                        const items = groupNodes.map(div => {
                            const img = div.querySelector('img');
                            return { url: img ? img.src : '', description: '' };
                        }).filter(x => x.url);
                        
                        if (items.length > 0) {
                        const mount = document.createElement('div');
                            mount.classList.add('ibundle-processed'); // Mark as processed
                        container.insertBefore(mount, ptr);
                            
                            // Safely remove nodes
                            const nodesToRemove = [];
                        let removeNode = ptr;
                        while (removeNode !== cursor){
                                nodesToRemove.push(removeNode);
                                removeNode = removeNode.nextElementSibling;
                            }
                            
                            // Remove nodes after mounting to avoid DOM issues
                            setTimeout(() => {
                                nodesToRemove.forEach(node => {
                                    try {
                                        if (node && node.parentNode === container) {
                                            container.removeChild(node);
                                        }
                                    } catch (error) {
                                        // Silent error handling
                                    }
                                });
                            }, 0);
                            
                            try { 
                                const root = createRoot(mount);
                                root.render(React.createElement(InlineBundleCarousel, { items }));
                            } catch(error) {
                                // Silent error handling
                            }
                        return true; // mutated, rerun
                        }
                    }
                    ptr = cursor; // skip run
                } else {
                    ptr = ptr.nextElementSibling;
                }
            }
            return false;
        };


        // Enhanced image grouping - find consecutive images
        try {
            const allImages = Array.from(container.querySelectorAll('.rt.rt-img'));
            
            if (allImages.length > 1) {
                // Group consecutive images
                const groups = [];
                let currentGroup = [];
                
                for (let i = 0; i < allImages.length; i++) {
                    const currentImg = allImages[i];
                    const nextImg = allImages[i + 1];
                    
                    currentGroup.push(currentImg);
                    
                    // Check if next image is consecutive
                    if (!nextImg || !isConsecutive(currentImg, nextImg)) {
                        if (currentGroup.length > 1) {
                            groups.push([...currentGroup]);
                        }
                        currentGroup = [];
                    }
                }
                
                // Create carousels for each group
                for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
                    const group = groups[groupIndex];
                    const items = group.map((div) => {
                        const img = div.querySelector('img');
                        if (!img || !img.src) return null;
                        
                        // Get ImageTitle from the comprehensive map first
                        let imageTitle = imageTitleMap.get(img.src);
                        
                        // If not found in map, try to find it in the global asset map
                        if (!imageTitle) {
                            const globalAssetData = globalAssetMap.get(img.src);
                            if (globalAssetData) {
                                imageTitle = globalAssetData.title;
                            }
                        }
                        
                        // If still no title, try to find it in the references directly
                        if (!imageTitle && references && Array.isArray(references)) {
                            
                            for (const ref of references) {
                                if (ref.__typename === 'ImageBundle' && ref.images) {
                                    for (const refImg of ref.images) {
                                        if (refImg.url === img.src) {
                                            imageTitle = refImg.imageTitle;
                                            break;
                                        }
                                    }
                                } else if (ref.__typename === 'Asset' && ref.url === img.src) {
                                    imageTitle = ref.imageTitle;
                                    break;
                                }
                                if (imageTitle) break;
                            }
                        }
                        
                        // Fallback to alt text if still no title
                        if (!imageTitle && img.alt && img.alt !== 'post-image') {
                            imageTitle = img.alt;
                        }
                        
                        // Final fallback - use a generic or filename-based title
                        if (!imageTitle) {
                            imageTitle = getFallbackImageTitle(img.src);
                        }
                        
                        return { 
                            url: img.src, 
                            description: imageTitle 
                        };
                    }).filter(Boolean);
                    
                    const validItems = items.filter(x => x && x.url);
                    
                    if (validItems.length > 1) {
                        const mount = document.createElement('div');
                        mount.classList.add('ibundle-processed');
                        container.insertBefore(mount, group[0]);
                        
                        // Remove original images
                        group.forEach(node => {
                            if (node.parentNode === container) {
                                container.removeChild(node);
                            }
                        });
                        
                        // Create carousel
                        try {
                            const root = createRoot(mount);
                            root.render(React.createElement(InlineBundleCarousel, { items: validItems }));
                        } catch(error) {
                            // Silent error handling
                        }
                    }
                }
            }
            
        } catch(error) {
            // Silent error handling
        }
        
        // Helper function to check if images are consecutive
        function isConsecutive(img1, img2) {
            if (!img1 || !img2) return false;
            return img1.nextElementSibling === img2 || 
                   (img1.nextElementSibling && img1.nextElementSibling.nextElementSibling === img2);
        }
        
        // Ensure headings have stable, non-empty ids for anchor navigation
        try { 
            ensureHeadingIds(); 
        } catch(error) {
            // Silent error handling
        }

        // Enhanced Twitter post grouping - find consecutive tweet containers
        try {
            const allTweets = Array.from(container.querySelectorAll('.tweet-container:not(.tbundle-processed)'));
            if (allTweets.length > 1) {
                const tweetGroups = [];
                let currentGroup = [];

                for (let i = 0; i < allTweets.length; i++) {
                    const currentTweet = allTweets[i];
                    const nextTweet = allTweets[i + 1];

                    currentGroup.push(currentTweet);

                    if (!nextTweet || !isConsecutive(currentTweet, nextTweet)) {
                        if (currentGroup.length > 1) {
                            tweetGroups.push([...currentGroup]);
                        }
                        currentGroup = [];
                    }
                }

                for (let groupIndex = 0; groupIndex < tweetGroups.length; groupIndex++) {
                    const group = tweetGroups[groupIndex];
                    const urls = group.map(div => div.getAttribute('data-tweet-url')).filter(Boolean);

                    if (urls.length > 1) {
                        const mount = document.createElement('div');
                        mount.classList.add('tbundle-processed');
                        container.insertBefore(mount, group[0]);

                        group.forEach(node => {
                            if (node.parentNode === container) {
                                container.removeChild(node);
                            }
                        });

                        try {
                            const root = createRoot(mount);
                            root.render(React.createElement(TwitterSlideshow, { tweets: urls }));
                        } catch(error) {
                            // Silent error handling
                        }
                    }
                }
            }
        } catch(error) {
            // Silent error handling
        }

        // Enhanced Instagram post grouping - find consecutive instagram containers
        try {
            const allInsta = Array.from(container.querySelectorAll('.instagram-embed-container:not(.instabundle-processed)'));
            if (allInsta.length > 1) {
                const instaGroups = [];
                let currentGroup = [];

                for (let i = 0; i < allInsta.length; i++) {
                    const current = allInsta[i];
                    const next = allInsta[i + 1];

                    currentGroup.push(current);

                    if (!next || !isConsecutive(current, next)) {
                        if (currentGroup.length > 1) {
                            instaGroups.push([...currentGroup]);
                        }
                        currentGroup = [];
                    }
                }

                for (let groupIndex = 0; groupIndex < instaGroups.length; groupIndex++) {
                    const group = instaGroups[groupIndex];
                    const urls = group.map(div => div.getAttribute('data-instagram-url')).filter(Boolean);

                    if (urls.length > 1) {
                        const mount = document.createElement('div');
                        mount.classList.add('instabundle-processed');
                        container.insertBefore(mount, group[0]);

                        group.forEach(node => {
                            if (node.parentNode === container) {
                                container.removeChild(node);
                            }
                        });

                        try {
                            const root = createRoot(mount);
                            root.render(React.createElement(InstagramSlideshow, { posts: urls }));
                        } catch(error) {
                            // Silent error handling
                        }
                    }
                }
            }
        } catch(error) {
            // Silent error handling
        }

        // Enhanced YouTube video grouping - find consecutive youtube containers
        try {
            const allYt = Array.from(container.querySelectorAll('.youtube-embed-wrapper:not(.ytbundle-processed)'));
            if (allYt.length > 1) {
                const ytGroups = [];
                let currentGroup = [];

                for (let i = 0; i < allYt.length; i++) {
                    const current = allYt[i];
                    const next = allYt[i + 1];

                    currentGroup.push(current);

                    if (!next || !isConsecutive(current, next)) {
                        if (currentGroup.length > 1) {
                            ytGroups.push([...currentGroup]);
                        }
                        currentGroup = [];
                    }
                }

                for (let groupIndex = 0; groupIndex < ytGroups.length; groupIndex++) {
                    const group = ytGroups[groupIndex];
                    const videoIds = group.map(div => div.getAttribute('data-youtube-videoid')).filter(Boolean);

                    if (videoIds.length > 1) {
                        const mount = document.createElement('div');
                        mount.classList.add('ytbundle-processed');
                        container.insertBefore(mount, group[0]);

                        group.forEach(node => {
                            if (node.parentNode === container) {
                                container.removeChild(node);
                            }
                        });

                        try {
                            const root = createRoot(mount);
                            root.render(React.createElement(YouTubeSlideshow, { videoIds: videoIds }));
                        } catch(error) {
                            // Silent error handling
                        }
                    }
                }
            }
        } catch(error) {
            // Silent error handling
        }

        // Process remaining single images (no carousel, just clean display)
        const singles = Array.from(container.querySelectorAll('.rt.rt-img:not(.ibundle-processed)'));
        singles.forEach(node => {
            const img = node.querySelector('img');
            if (!img) return;
            
            // Just mark as processed, don't create carousel for single images
            node.classList.add('ibundle-processed');
        });
        
        // Ensure headings have stable, non-empty ids for anchor navigation
        try { 
            ensureHeadingIds(); 
        } catch(error) {
            // Silent error handling
        }
    }, [content, references]); // eslint-disable-line react-hooks/exhaustive-deps

    // Validate content structure before rendering
    // RichText requires content to have a 'children' property
    if (!content || (typeof content === 'object' && !content.children && !content.content && !Array.isArray(content))) {
        // Invalid content structure - handled below
        // If content is invalid, try to render as HTML if available
        if (typeof content === 'string') {
            return <div ref={containerRef} className="rich-text-comp" dangerouslySetInnerHTML={{ __html: content }} />;
        }
        // If content is an object but doesn't have children, try to render it
        if (typeof content === 'object' && content.html) {
            return <div ref={containerRef} className="rich-text-comp" dangerouslySetInnerHTML={{ __html: content.html }} />;
        }
        return <div ref={containerRef} className="rich-text-comp">Content not available</div>;
    }

    // Helper to transform custom nodes (like twitterEmbed) into standard nodes
    const transformContent = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return nodes;
        
        return nodes.map(node => {
            if (node.type === 'twitterEmbed') {
                console.log('ðŸ” Found twitterEmbed node, converting to paragraph:', node);
                if (node.attrs && node.attrs.url) {
                    // Convert to standard paragraph
                    return {
                        type: 'paragraph',
                        children: [{
                            text: `[TWEET_EMBED: ${node.attrs.url}]`
                        }]
                    };
                }
            }
            
            // Handle recursion for both 'children' (GraphCMS) and 'content' (TipTap)
            if (node.children) {
                return {
                    ...node,
                    children: transformContent(node.children)
                };
            }
            
            if (node.content) {
                return {
                    ...node,
                    content: transformContent(node.content)
                };
            }
            
            return node;
        });
    };

    // Ensure content has children property (required by RichText)
    // Also transform custom nodes into compatible format
    let transformedContent = content;
    console.log('ðŸ” Raw content received:', content);

    if (Array.isArray(content)) {
        transformedContent = transformContent(content);
    } else if (content) {
        if (content.children) {
            transformedContent = { ...content, children: transformContent(content.children) };
        } else if (content.content) {
            // If it has 'content' (TipTap doc), transform it
            // Note: RichText might expect 'children', so we might need to map 'content' to 'children' 
            // but for now let's just transform the values inside, assuming the renderer handles the keys.
            // If the renderer STRICTLY requires 'children', we might need to alias it, 
            // but let's assume validContent logic below handles the root alias.
            transformedContent = { ...content, content: transformContent(content.content) };
        }
    }

    console.log('ðŸ” Transformed content:', transformedContent);

    const validContent = Array.isArray(transformedContent) ? 
        { children: transformedContent } : 
        (transformedContent && transformedContent.content ? 
            { ...transformedContent, children: transformedContent.content } : // Alias content to children for RichText root
            transformedContent);
    
    // Helper function to render cell content directly
    const renderCellContent = (cellChildren) => {
        if (!cellChildren || !Array.isArray(cellChildren)) {
            return null;
        }
        
        return cellChildren.map((child, idx) => {
            if (typeof child === 'string') {
                return <span key={idx}>{child}</span>;
            }
            
            if (child && child.text) {
                // Text node with marks - apply all marks properly
                let content = child.text;
                if (child.code) {
                    content = <code key={idx}>{content}</code>;
                }
                if (child.underline && !child.code) {
                    content = <u key={idx}>{content}</u>;
                }
                if (child.italic) {
                    content = <em key={idx}>{content}</em>;
                }
                if (child.bold) {
                    content = <strong key={idx}>{content}</strong>;
                }
                // If no marks, return plain text
                if (!child.bold && !child.italic && !child.underline && !child.code) {
                    return <span key={idx}>{content}</span>;
                }
                return content;
            }
            
            if (child && child.type === 'link') {
                const linkContent = child.children && child.children.map((linkChild, linkIdx) => {
                    if (typeof linkChild === 'string') return linkChild;
                    if (linkChild && linkChild.text) {
                        let textContent = linkChild.text;
                        if (linkChild.bold) textContent = <strong key={linkIdx}>{textContent}</strong>;
                        if (linkChild.italic) textContent = <em key={linkIdx}>{textContent}</em>;
                        if (linkChild.underline) textContent = <u key={linkIdx}>{textContent}</u>;
                        return textContent;
                    }
                    return null;
                });
                
                return (
                    <a 
                        key={idx}
                        href={child.url || '#'} 
                        target={child.openInNewTab ? '_blank' : '_self'} 
                        rel={child.openInNewTab ? 'noopener noreferrer' : undefined}
                    >
                        <LinkFavicon href={child.url} />
                        {linkContent}
                    </a>
                );
            }
            
            return null;
        }).filter(Boolean);
    };
    
    // Helper function to render a table node manually
    const renderTableNode = (tableNode, tableIndex) => {
        if (!tableNode || !tableNode.children || !Array.isArray(tableNode.children)) {
            console.error('ðŸ” [Table Manual Render] Invalid table node');
            return null;
        }
        
        console.log(`ðŸ” [Table Manual Render] Rendering table ${tableIndex} with`, tableNode.children.length, 'rows');
        
        const headerRows = [];
        const bodyRows = [];
        
        tableNode.children.forEach((rowNode, rowIndex) => {
            const isHeaderRow = rowNode && rowNode.isHeaderRow;
            console.log(`ðŸ” [Table Manual Render] Row ${rowIndex} isHeaderRow:`, isHeaderRow);
            
            if (!rowNode || !rowNode.children || !Array.isArray(rowNode.children)) {
                console.warn(`ðŸ” [Table Manual Render] Row ${rowIndex} has no children`);
                return;
            }
            
            // Render cells directly
            const cells = rowNode.children.map((cellNode, cellIndex) => {
                if (!cellNode || cellNode.type !== 'table_cell') {
                    console.warn(`ðŸ” [Table Manual Render] Row ${rowIndex}, Cell ${cellIndex} is not a table_cell`);
                    return null;
                }
                
                const cellContent = renderCellContent(cellNode.children);
                const isHeader = cellNode.tableCellType === 'header';
                
                const CellTag = isHeader ? 'th' : 'td';
                const cellClassName = isHeader ? 'custom-th' : 'custom-td';
                
                return (
                    <CellTag key={`table-${tableIndex}-row-${rowIndex}-cell-${cellIndex}`} className={cellClassName}>
                        {cellContent}
                    </CellTag>
                );
            }).filter(Boolean);
            
            const rowElement = (
                <tr key={`table-${tableIndex}-row-${rowIndex}`} className="custom-tr">
                    {cells}
                </tr>
            );
            
            if (isHeaderRow) {
                headerRows.push(rowElement);
            } else {
                bodyRows.push(rowElement);
            }
        });
        
        console.log(`ðŸ” [Table Manual Render] Table ${tableIndex} - Header rows:`, headerRows.length, 'Body rows:', bodyRows.length);
        console.log(`ðŸ” [Table Manual Render] Rendering table with ${headerRows.length} header rows and ${bodyRows.length} body rows`);
        
        const tableElement = (
            <div key={`table-wrapper-${tableIndex}`} className="table-wrapper">
                <table className="rt rt-table">
                    {headerRows.length > 0 && <thead className="custom-thead">{headerRows}</thead>}
                    {bodyRows.length > 0 && <tbody className="custom-tbody">{bodyRows}</tbody>}
                </table>
            </div>
        );
        
        console.log('ðŸ” [Table Manual Render] Table element created:', tableElement);
        
        return tableElement;
    };
    
    // Find all table nodes for manual rendering
    const tableNodes = [];
    if (validContent && validContent.children) {
        validContent.children.forEach(child => {
            if (child && child.type === 'table') {
                tableNodes.push(child);
            }
        });
    }
    
    return (
        <div ref={containerRef} className="rich-text-comp">
            <RichText content={validContent} references={references || []}
                renderers={{

                    p: ({ children, ...props }) => {
                        console.log('ðŸ” [Paragraph Debug] Props:', props);
                        console.log('ðŸ” [Paragraph Debug] Children:', children);

                        // Extract text content from children - handle various structures
                        let textContent = '';
                        
                        
                        // CRITICAL: Check the node structure first (GraphCMS RichText passes original node)
                        // Helper to extract text recursively
                        const getRecursiveText = (nodes) => {
                             if (!nodes) return '';
                             if (typeof nodes === 'string') return nodes;
                             if (Array.isArray(nodes)) {
                                 return nodes.map(getRecursiveText).join('');
                             }
                             if (React.isValidElement(nodes) && nodes.props) {
                                 return getRecursiveText(nodes.props.children);
                             }
                             return '';
                        };

                        // Primary: Check props.node (GraphCMS structure)
                        if (props.node && props.node.children && Array.isArray(props.node.children)) {
                            // Extract text from node children
                            for (const child of props.node.children) {
                                if (child && child.text) {
                                    textContent += child.text;
                                }
                            }
                        }
                        
                        // Fallback: Check React children if node content is empty
                        if (!textContent && children) {
                            textContent = getRecursiveText(children);
                        }
                        
                        console.log('ðŸ” [Paragraph Debug] Extracted text content:', textContent);
                        
                        // Check for Tweet Embed Marker
                        // Regex for tweet marker: 
                        // 1. Standard HTML comment: <!-- TWEET: url -->
                        // 2. Escaped HTML comment (common when pasting in visual editor): &lt;!-- TWEET: url --&gt;
                        // 3. Plain Text Marker: [TWEET_EMBED: url]
                        if (textContent && (textContent.includes('TWEET:') || textContent.includes('TWEET_EMBED') || textContent.includes('twitter.com') || textContent.includes('x.com'))) {
                             console.log('ðŸ” [Paragraph Debug] Potential tweet marker found in text:', textContent);
                        }
                        
                        const tweetMatch = textContent && textContent.match(/(?:<!--|&lt;!--)\s*TWEET:\s*(https:\/\/(?:twitter\.com|x\.com)\/[^ ]+)\s*(?:-->|--&gt;)|\[TWEET_EMBED:\s*(https:\/\/(?:twitter\.com|x\.com)\/[^ \]]+)\s*\]/);
                        
                        if (tweetMatch) {
                            const tweetUrl = tweetMatch[1] || tweetMatch[2]; // match[1] is comment, match[2] is bracket
                            console.log('ðŸ” [Paragraph Debug] Tweet match confirmed:', tweetUrl);
                            const urlParts = tweetUrl.split('/');
                            const tweetId = urlParts[urlParts.length - 1].split('?')[0];
                            
                            return (
                                <div className="tweet-container" data-tweet-url={tweetUrl} style={{ display: 'flex', justifyContent: 'center', margin: '20px auto', width: '100%', maxWidth: '550px' }}>
                                    <TwitterTweetEmbed tweetId={tweetId} />
                                </div>
                            );
                        }

                        // Check if this paragraph contains an image placeholder
                        // Match __IMAGE_PLACEHOLDER_X__ (exact format we create)
                        if (textContent && textContent.includes('IMAGE_PLACEHOLDER')) {
                            // Try exact match first: __IMAGE_PLACEHOLDER_2__
                            let placeholderMatch = textContent.match(/__IMAGE_PLACEHOLDER_(\d+)__/);
                            
                            // If no match, try lenient match (in case of extra underscores)
                            if (!placeholderMatch) {
                                placeholderMatch = textContent.match(/IMAGE_PLACEHOLDER[_\s]*(\d+)/);
                            }
                            
                            if (placeholderMatch) {
                                const placeholderIndex = parseInt(placeholderMatch[1], 10);
                                
                                // Check if this index belongs to a group with multiple images
                                const group = imageGroups.find(g => g.some(item => item.index === placeholderIndex));
                                
                                if (group && group.length > 1) {
                                    // It's part of a group!
                                    const isFirstInGroup = group[0].index === placeholderIndex;
                                    
                                    if (isFirstInGroup) {
                                        // Render slideshow for the group
                                        const images = group.map(item => item.imageData);
                                        return <ImageSlideshow images={images} />;
                                    } else {
                                        // Hide subsequent images in the group (they are part of the slideshow)
                                        return null;
                                    }
                                }

                                // Fallback to single image logic
                                const imageData = imageDataMap.get(placeholderIndex);
                                
                                if (imageData && imageData.src) {
                                    // Render as single image with title (fallback if carousel grouping didn't work)
                                    // Return the image component directly - this replaces the paragraph content
                                    return (
                                        <div className="ibundle">
                                            <div className="ibundle-frame">
                                                <img 
                                                    className="ibundle-image"
                                                    src={imageData.src} 
                                                    alt={imageData.alt || imageData.title || 'post-image'} 
                                                />
                                            </div>
                                            <div className="ibundle-controls">
                                                <div className="ibundle-desc">{imageData.title || imageData.alt || 'Image'}</div>
                                            </div>
                                        </div>
                                    );
                                } else {
                                    // Return null to prevent placeholder text from showing
                                    return null;
                                }
                            } else {
                                // If placeholder text is detected but regex didn't match, still hide it
                                if (textContent && textContent.includes('IMAGE_PLACEHOLDER')) {
                                    return null;
                                }
                            }
                        }
                        
                        // If no placeholder match but text is empty or just whitespace, check if it might be an image paragraph
                        const childrenArray = React.Children.toArray(children);
                        if (!textContent.trim() && childrenArray.length === 0) {
                            // This might be an empty paragraph that was meant to be an image
                            // Skip rendering it
                            return null;
                        }
                        
                        // Also check if the paragraph has the hidden class (already processed by fallback)
                        if (props.node && props.node.className && props.node.className.includes('ibundle-placeholder-hidden')) {
                            return null;
                        }
                        
                        const childArray = childrenArray;
                        const hasBlockEmbed = childArray.some((child) => {
                            if (!React.isValidElement(child)) return false;
                            const className = child.props && child.props.className ? String(child.props.className) : "";
                            return (
                                className.includes("ibundle") ||
                                className.includes("rt rt-img") ||
                                className.includes("table-wrapper")
                            );
                        });
                        return hasBlockEmbed ? <>{children}</> : <p className="rt rt-para">{children}</p>;
                    }, h1: ({ children, ...props }) => {
                        const text = React.Children.toArray(children).map(child => {
                            if (typeof child === 'string') return child;
                            if (React.isValidElement(child) && child.props && child.props.children) {
                                return React.Children.toArray(child.props.children).join('');
                            }
                            return '';
                        }).join('');
                        const id = text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '') || 'h1';
                        return <h1 id={id} className="rt rt-h1">{children}</h1>;
                    },
                    h2: ({ children, ...props }) => {
                        const text = React.Children.toArray(children).map(child => {
                            if (typeof child === 'string') return child;
                            if (React.isValidElement(child) && child.props && child.props.children) {
                                return React.Children.toArray(child.props.children).join('');
                            }
                            return '';
                        }).join('');
                        const id = text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '') || 'h2';
                        return <h2 id={id} className="rt rt-h2">{children}</h2>;
                    },
                    h3: ({ children, ...props }) => {
                        const text = React.Children.toArray(children).map(child => {
                            if (typeof child === 'string') return child;
                            if (React.isValidElement(child) && child.props && child.props.children) {
                                return React.Children.toArray(child.props.children).join('');
                            }
                            return '';
                        }).join('');
                        const id = text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '') || 'h3';
                        return <h3 id={id} className="rt rt-h3">{children}</h3>;
                    },
                    h4: ({ children }) => { return <h4 className="rt rt-h4">{children}</h4>; },
                    h5: ({ children }) => { return <h5 className="rt rt-h5">{children}</h5>; },
                    h6: ({ children }) => { return <h6 className="rt rt-h6">{children}</h6>; },
                    ul: ({ children }) => <ul className="rt rt-ul">{children}</ul>,
                    ol: ({ children }) => <ol className="rt rt-ol">{children}</ol>,
                    li: ({ children }) => <li className="rt rt-li">{children}</li>,
                    img: ({ src, alt, title }) => {
                        return <div className="rt rt-img"><img src={src} alt={alt || title || 'post-image'} title={title || alt || 'post-image'} /></div>;
                    },
                    table: ({ children, ...props }) => {
                        console.log('ðŸ” [Table Renderer] Rendering table');
                        console.log('ðŸ” [Table Renderer] Props.node:', props.node);
                        
                        // Try to get table node from props.node first
                        let tableNode = props.node;
                        
                        // If props.node is undefined (MongoDB posts), use tableNodes array
                        if (!tableNode && tableNodes.length > 0) {
                            const currentIndex = tableIndexRef.current;
                            if (currentIndex < tableNodes.length) {
                                tableNode = tableNodes[currentIndex];
                                console.log(`ðŸ” [Table Renderer] Using table from tableNodes[${currentIndex}]`);
                                tableIndexRef.current = currentIndex + 1; // Increment for next table
                            }
                        }
                        
                        // If we have table node data, render it manually
                        if (tableNode && tableNode.children && Array.isArray(tableNode.children)) {
                            const renderedTable = renderTableNode(tableNode, tableIndexRef.current - 1);
                            console.log('ðŸ” [Table Renderer] Returning rendered table:', renderedTable);
                            if (!renderedTable) {
                                console.error('ðŸ” [Table Renderer] renderTableNode returned null!');
                            }
                            return renderedTable;
                        }
                        
                        // Fallback: try to use children if node data not available
                        const rows = React.Children.toArray(children);
                        console.log('ðŸ” [Table Renderer] Fallback: Using children, count:', rows.length);
                        
                        return (
                            <div className="table-wrapper">
                                <table className="rt rt-table">
                                    <tbody className="custom-tbody">{rows}</tbody>
                                </table>
                            </div>
                        );
                    },
                    thead: ({ children }) => <thead className="custom-thead">{children}</thead>,
                    tbody: ({ children }) => <tbody className="custom-tbody">{children}</tbody>,
                    tr: ({ children, ...props }) => {
                        // Log tr renderer to see what data is available
                        console.log('ðŸ” [TR Renderer] Rendering tr, props:', props);
                        console.log('ðŸ” [TR Renderer] TR props.node:', props.node);
                        
                        return <tr className="custom-tr">{children}</tr>;
                    },
                    table_cell: ({ children, tableCellType }) => {
                        const cleanedChildren = React.Children.map(children, (child) => { if (child && child.type === "p") return child.props.children; return child; });
                        return tableCellType === "header" ? (<th className="custom-th">{cleanedChildren}</th>) : (<td className="custom-td">{cleanedChildren}</td>);
                    },
                    bold: ({ children }) => <strong className="rt rt-bold">{children}</strong>,
                    blockquote: ({ children }) => (
                        <blockquote className="rt rt-blockquote">
                            <div className="blockquote-icon">
                                <i className="fi fi-rr-quote-right"></i>
                            </div>
                            <div className="blockquote-content">
                                {children}
                            </div>
                        </blockquote>
                    ),
                    a: ({ href, children, openInNewTab}) => <a className="rt rt-link" href={href} target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}><LinkFavicon href={href} />{children}</a>,
                    embed: {
                        ImageBundle: (props) => {
                            const images = Array.isArray(props.images) ? props.images : [];
                            
                            const normalized = images
                                .map((img) => {
                                    return {
                                    url: img && img.url ? img.url : '',
                                    description: (img && img.imageTitle) || ''
                                    };
                                })
                                .filter(i => i.url);
                            
                            if (normalized.length === 0) return null;
                            if (normalized.length === 1) {
                                const item = normalized[0];
                                return (
                                    <div className="ibundle">
                                        <div className="ibundle-frame"><img className="ibundle-image" src={item.url} alt={item.description || ''} /></div>
                                        <div className="ibundle-controls"><div className="ibundle-desc">{item.description}</div></div>
                                    </div>
                                );
                            }
                            return (<InlineBundleCarousel items={normalized} />);
                        },
                        Asset: (props) => {
                            // Handle single image embeds converted from TipTap
                            const url = props.url || (props.src) || '';
                            const alt = props.altText || props.alt || props.title || 'post-image';
                            const title = props.title || props.altText || props.alt || 'post-image';
                            
                            if (!url) {
                                return null;
                            }
                            
                            
                            return (
                                <div className="rt rt-img">
                                    <img src={url} alt={alt} title={title} />
                                </div>
                            );
                        }
                    }
                }}
            />
        </div>
    );
}

// Wrap GroupedRichText in React.memo() to prevent unnecessary re-renders
// This is CRITICAL - without this, every scroll/TOC change/image operation re-renders the component
// eslint-disable-next-line no-unused-vars
const MemoizedGroupedRichText = React.memo(GroupedRichText);

// Table of content function to extract headings from rich text JSON
function extractHeadings(richTextJson) {
  const headings = [];

  if (
    !richTextJson ||
    !richTextJson.children ||
    !Array.isArray(richTextJson.children)
  ) {
    return [];
  }

  // Helper function to extract text from a node (handles nested structures)
  const extractText = (node) => {
    if (!node) return '';
    if (node.text) return node.text;
    if (node.children && Array.isArray(node.children)) {
      return node.children.map(child => extractText(child)).join('');
    }
    return '';
  };

  for (let i = 0; i < richTextJson.children.length; i++) {
    const node = richTextJson.children[i];

    if (
      node.type === "heading-one" ||
      node.type === "heading-two" ||
      node.type === "heading-three" ||
      node.type === "heading-four" ||
      node.type === "heading-five" ||
      node.type === "heading-six"
    ) {
      // Extract text from all children (handles nested text nodes)
      const text = extractText(node);

      if (text && text.trim()) {
        const id = text
          .toLowerCase()
          .replace(/[^\w]+/g, "-")
          .replace(/^-+|-+$/g, "");

        headings.push({
          text: text.trim(),
          id: id,
          level: node.type, // optionally use to style differently
        });
      }
    }
  }

  return headings;
}

import { robustFetch } from "../utils/apiUtils";


export default function Post(){
    useFavicon("/images/home_images/Blog-page-Logo.png");
    let params = useParams();
    const navigate = useNavigate();
    
    // Handle both old format (/blog/:blogid) and new format (/blog/:categoryName/:slug)
    // Process slug BEFORE using it in useEffect
    let get_slug;
    let urlCategoryName = null;
    if (params.slug) {
        // New format: /blog/:categoryName/:slug
        get_slug = params.slug;
        urlCategoryName = params.categoryName; // Get category from URL
    } else {
        // Old format: /blog/:blogid
        get_slug = Object.values(params).join();
    }
    
    let [value, setValue] = useState(0); // eslint-disable-line no-unused-vars
    const [commentCount, setCommentCount] = useState(0); // eslint-disable-line no-unused-vars
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [tocVisible, setTocVisible] = useState(false);
    const tocVisibleRef = useRef(false);
    
    // ========== MONGODB POSTS FETCHING START ==========
    // Separate state for MongoDB post data
    const [mongoPostData, setMongoPostData] = useState(null);
    const [mongoLoading, setMongoLoading] = useState(true);
    const [mongoError, setMongoError] = useState(null); // eslint-disable-line no-unused-vars
    const [isHardNotFound, setIsHardNotFound] = useState(false);
    const [hasTransientFetchError, setHasTransientFetchError] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(null);

    

    // Fetch post from MongoDB BlogDb
    useEffect(() => {
        const fetchMongoPost = async () => {
            try {
                setMongoLoading(true);
                setMongoError(null);
                setIsHardNotFound(false);
                setHasTransientFetchError(false);
                
                const response = await robustFetch(API_ENDPOINTS.BLOG.POST_BY_SLUG(get_slug));
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setMongoPostData(null);
                        setIsHardNotFound(true);
                        setMongoLoading(false);
                        return;
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const postData = await response.json();
                
                // Guard against invalid/empty responses
                if (!postData || typeof postData !== 'object') {
                    setMongoError('Server returned an empty or invalid response. Please refresh.');
                    setHasTransientFetchError(true);
                    setMongoLoading(false);
                    return;
                }
                
                // Only set data if isPublished is true
                if (postData && postData.isPublished === true) {
                    // Check if URL category matches actual category from post
                    const category = postData.category;
                    let actualCategorySlug = 'uncategorized';
                    let hasCategory = false;
                    
                    // Check if category exists and is populated
                    if (category) {
                        // If it's an object (populated category)
                        if (typeof category === 'object' && category !== null && !Array.isArray(category)) {
                            // Check if it has slug property (preferred)
                            if (category.slug && typeof category.slug === 'string' && category.slug.length > 0) {
                                hasCategory = true;
                                actualCategorySlug = category.slug;
                            } 
                            // If no slug, check for name property
                            else if (category.name && typeof category.name === 'string' && category.name.length > 0) {
                                hasCategory = true;
                                actualCategorySlug = category.name.toLowerCase().replace(/\s+/g, '-');
                            }
                            // If category object exists but has no slug/name, it might be incomplete
                            // But if it has _id, it means it's a reference that exists
                            else if (category._id) {
                                // Category reference exists but no slug/name - can't determine slug
                                hasCategory = false;
                            }
                        } 
                        // If it's a string, it's likely an ObjectId that wasn't populated
                        // Backend should handle this now, but just in case
                        else if (typeof category === 'string' && category.length === 24) {
                            hasCategory = false;
                        }
                    }
                    
                    // Check if redirect is needed - but don't redirect if we're already on the correct URL
                    // Only redirect if there's a clear mismatch
                    const needsRedirect = 
                        (hasCategory && urlCategoryName === 'uncategorized') ||
                        (hasCategory && urlCategoryName && urlCategoryName !== actualCategorySlug) ||
                        (!hasCategory && urlCategoryName && urlCategoryName !== 'uncategorized');
                    
                    if (needsRedirect) {
                        // Use navigate instead of window.location.replace for smoother transition
                        const correctUrl = hasCategory ? `/blog/${actualCategorySlug}/${get_slug}` : `/blog/uncategorized/${get_slug}`;
                        // Don't set loading to false - keep it true to show loader during redirect
                        navigate(correctUrl, { replace: true });
                        // The component will re-render with new URL and fetch data again
                        return;
                    }
                    
                    setMongoPostData(postData);
                    setMongoLoading(false);
                } else {
                    // Post exists but is not published
                    setMongoPostData(null);
                    setIsHardNotFound(true);
                }
                setMongoLoading(false);
            } catch (err) {
                setMongoError(err);
                setMongoPostData(null);
                setHasTransientFetchError(true);
                setMongoLoading(false);
            }
        };

        if (get_slug) {
            fetchMongoPost();
        } else {
            setMongoLoading(false);
        }
    }, [get_slug, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
    // ========== MONGODB POST FETCHING END ==========

    // Note: Category redirect is now handled in the fetchMongoPost useEffect above
    // This backup check is no longer needed as redirect happens immediately after data fetch

    // Handle TOC visibility changes with stable state management
    // eslint-disable-next-line no-unused-vars
    const handleTocVisibilityChange = useCallback((visible) => {
        if (tocVisibleRef.current !== visible) {
            tocVisibleRef.current = visible;
            setTocVisible(visible);
        }
    }, []);

    // Update body class based on TOC visibility with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (tocVisible) {
                document.body.classList.add('post-page-toc-visible');
            } else {
                document.body.classList.remove('post-page-toc-visible');
            }
        }, 200); // Increased debounce time

        return () => {
            clearTimeout(timeoutId);
            document.body.classList.remove('post-page-toc-visible');
        };
    }, [tocVisible]);


    // Handle popup close
    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setSelectedAuthors([]);
    };
    
    // Post data loaded successfully

    // Promotions state loaded

    useEffect(() => {
        if (!mongoPostData || !mongoPostData.node || !mongoPostData.node.id) return;
        fetch(API_ENDPOINTS.BLOG.THOUGHTS_COUNT(mongoPostData.node.id))
            .then(res => res.json())
            .then(data => setCommentCount(data.count || 0))
            .catch(() => setCommentCount(0));
    }, [mongoPostData, get_slug]);



    // Add hover effect functionality for author highlighting
    useEffect(() => {
        const handleAuthorImageHover = (e) => {
            const authorIndex = e.target.dataset.authorIndex;
            const authorName = e.target.dataset.authorName;
            
            if (authorIndex !== undefined && authorName) {
                // Highlight corresponding author name
                const authorNameSpans = document.querySelectorAll(`[data-author-name="${authorName}"]`);
                authorNameSpans.forEach(span => {
                    if (span.classList.contains('author-name')) {
                        span.classList.add('author-highlighted');
                    }
                });
                
                // Add highlight to hovered image
                e.target.classList.add('author-img-highlighted');
            }
        };

        const handleAuthorImageLeave = (e) => {
            const authorName = e.target.dataset.authorName;
            
            if (authorName) {
                // Remove highlight from author name
                const authorNameSpans = document.querySelectorAll(`[data-author-name="${authorName}"]`);
                authorNameSpans.forEach(span => {
                    if (span.classList.contains('author-name')) {
                        span.classList.remove('author-highlighted');
                    }
                });
                
                // Remove highlight from image
                e.target.classList.remove('author-img-highlighted');
            }
        };

        // Add event listeners to all author images
        const authorImages = document.querySelectorAll('.author-img[data-author-name]');
        authorImages.forEach(img => {
            img.addEventListener('mouseenter', handleAuthorImageHover);
            img.addEventListener('mouseleave', handleAuthorImageLeave);
        });

        // Define hover handlers for author names
        const handleAuthorNameHover = (e) => {
            const authorName = e.target.dataset.authorName;
            if (authorName) {
                // Highlight the hovered name
                e.target.classList.add('author-highlighted');
                
                // Highlight corresponding author image
                const authorImage = document.querySelector(`.author-img[data-author-name="${authorName}"]`);
                if (authorImage) {
                    authorImage.classList.add('author-img-highlighted');
                }
            }
        };

        const handleAuthorNameLeave = (e) => {
            const authorName = e.target.dataset.authorName;
            if (authorName) {
                // Remove highlight from the name
                e.target.classList.remove('author-highlighted');
                
                // Remove highlight from corresponding author image
                const authorImage = document.querySelector(`.author-img[data-author-name="${authorName}"]`);
                if (authorImage) {
                    authorImage.classList.remove('author-img-highlighted');
                }
            }
        };

        // Add event listeners to all author names
        const authorNameSpans = document.querySelectorAll('.author-name[data-author-name]');
        authorNameSpans.forEach(span => {
            span.addEventListener('mouseenter', handleAuthorNameHover);
            span.addEventListener('mouseleave', handleAuthorNameLeave);
        });

        // Cleanup function
        return () => {
            authorImages.forEach(img => {
                img.removeEventListener('mouseenter', handleAuthorImageHover);
                img.removeEventListener('mouseleave', handleAuthorImageLeave);
            });
            authorNameSpans.forEach(span => {
                span.removeEventListener('mouseenter', handleAuthorNameHover);
                span.removeEventListener('mouseleave', handleAuthorNameLeave);
            });
        };
    }, []);



    // ========== RENDER MONGODB POST IF FOUND (AFTER ALL HOOKS) ==========
    // Render MongoDB post if found - this must be AFTER all hooks are called
    if (mongoPostData) {
        return <MongoDbPostRenderer post={mongoPostData} get_slug={get_slug} />;
    }

    // If still loading MongoDB, show loader (Header/Footer are in routing.js)
    if (mongoLoading) {
        return <BookLoader progress={loadingProgress} />;
    }

    // Avoid accidental de-indexing when backend is temporarily unavailable.
    if (hasTransientFetchError && !isHardNotFound) {
        return (
            <div className="all-blog-area">
                <SEO
                    title="Blog Post"
                    description="This post is temporarily unavailable. Please try again shortly."
                    url={`https://www.harivishnu.in/blog/${urlCategoryName || 'uncategorized'}/${get_slug}`}
                />
                <div className="post-area">
                    <h1>Temporary Loading Issue</h1>
                    <p>We could not load this post right now. Please refresh in a moment.</p>
                </div>
            </div>
        );
    }

    // If we have data, it will be rendered above. If we reach here, no data was found.
    return (
        <div className="all-blog-area">
            <SEO title="Post Not Found" noIndex={isHardNotFound} />
            <div className="post-area">
                <h1>Post Not Found</h1>
                <p>The post you're looking for could not be found. Please check the URL and try again.</p>
            </div>
            
            {/* Author Popup for fallback/standard renderer */}
            {isPopupOpen && selectedAuthors.length > 0 && (
                <AuthorPopup 
                    authors={selectedAuthors} 
                    initialAuthorIndex={0}
                    isOpen={isPopupOpen} 
                    onClose={handlePopupClose} 
                />
            )}
        </div>
    );
};